import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { exportToCSV } from "react-easy-export/dist/react-easy-export";
import { exportOrdersToSheetWithItems } from "../../../../../utils/excelExport";
import { productApiService } from "../../../../../service/admin/productApiService";
import { orderApiService } from "../../../../../service/admin/ordersApiService";
import { userApiService } from "../../../../../service/admin/userApiService";
import { useLoader } from "../../../../../base/context/LoaderProvider";
import {
  ToastTypes,
  useMessage,
} from "../../../../../base/context/MessageProvider";
import ExcelTable from "../../../../library/excelTable";
import OrderPlatformBadge from "../../../../library/orderPlatformBadge/OrderPlatformBadge";
import { getOrderItemPricingMode } from "../../../../../utils/orderPricingMode";
import { getOrderPlatformMeta } from "../../../../../utils/orderMarketplacePlatform";
import usePermissions from "../../../../../hooks/usePermissions";
import {
  MdCreditCard,
  MdClose,
  MdCheckCircle,
  MdInfo,
  MdPeople,
  MdShoppingCart,
  MdAccountBalanceWallet,
  MdError,
  MdWarning,
  MdCalendarToday,
  MdFileDownload,
  MdAdd
} from "react-icons/md";
import { FiEye, FiEyeOff } from "react-icons/fi";
import AdminRaiseTicketModal from "../raiseTicketModal";
import TicketChatPanel from "../../../../library/ticketChatPanel";
import RefundModal from "../../RefundModel";
import { bankTransferApiService } from "../../../../../service/admin/bankTransferApiService";
import "./style.css";
import { getImPointsDiscount } from "../../../../../helper/orderImPoints";

const OrderDetailTable = ({ data, pages, limit, onColumnFilterChange, activeTab = 'all', onTabChange, mappedOrdersCount = 0, onFetchAllOrders, onRefresh, accountTypeFilter = '', onAccountTypeFilterChange, isLoading = false, sourceTab = '' }) => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();
  const { canEdit, canDelete } = usePermissions();
  const [uploadFile, setUploadFile] = useState(null);
  const [ticketModalOrderId, setTicketModalOrderId] = useState(null);
  const [ticketPanelTicketId, setTicketPanelTicketId] = useState(null);
  const [ticketPanelOrderLabel, setTicketPanelOrderLabel] = useState(null);
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
  const [paymentConfirmData, setPaymentConfirmData] = useState(null);
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState(null);
  const [showAllSuccessOrders, setShowAllSuccessOrders] = useState(false);
  const [showAllFailedOrders, setShowAllFailedOrders] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentProgress, setPaymentProgress] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ open: false, order: null, password: "", showPassword: false });
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ open: false, password: "", showPassword: false });
  const [exportDateRange, setExportDateRange] = useState({ startDate: "", endDate: "" });
  const [exporting, setExporting] = useState(false);
  const [confirmRejectModal, setConfirmRejectModal] = useState({ open: false, order: null, action: null });
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [rejectRefundOrder, setRejectRefundOrder] = useState(null);
  const [splitModal, setSplitModal] = useState({ open: false, order: null, selectedOfferIds: [], loading: false });
  const [splitSubmitting, setSplitSubmitting] = useState(false);
  const [btConfirmModal, setBtConfirmModal] = useState({ open: false, order: null, adminNote: "", submitting: false });
  const [isProcessingCrossBorder, setIsProcessingCrossBorder] = useState(false);
  const [orderWarnings, setOrderWarnings] = useState({}); // { orderId/upsaleoOrderId: [warning strings] }

  const currentPage = Number(pages?.currentPage) || 1;
  const pageSize = Number(limit) || 0;
  const effectivePageSize = pageSize || (data?.length ?? 0);

  // Validate processing orders on load — check for missing supplier/product info
  useEffect(() => {
    const processingOrders = (data || []).filter(order => order.status === "processing");
    if (processingOrders.length === 0) {
      setOrderWarnings({});
      return;
    }

    const allSkus = [...new Set(processingOrders.flatMap(order => {
      const skus = [];
      order.orderDetails?.forEach(item => {
        if (item.sku) skus.push(item.sku);
        if (item.ASIN) skus.push(item.ASIN);
        item.variants?.forEach(v => { if (v.skuId) skus.push(String(v.skuId)); });
        if (item.variant?.skuId) skus.push(String(item.variant.skuId));
      });
      return skus;
    }))].filter(Boolean);

    if (allSkus.length === 0) return;

    productApiService.getProductsBySkus(allSkus).then(productRes => {
      const products = productRes?.data?.products || [];
      const skuProductMap = {};
      products.forEach(product => {
        if (product.sku) skuProductMap[product.sku] = product;
        if (product.offerId) skuProductMap[String(product.offerId)] = product;
        product.variations?.forEach(v => {
          if (v.skuId) skuProductMap[String(v.skuId)] = product;
          v.mappedAsins?.forEach(asin => { if (asin) skuProductMap[asin] = product; });
        });
        product.amazonMappings?.forEach(m => { if (m.asin) skuProductMap[m.asin] = product; });
      });

      const findProduct = (item) => {
        if (item.sku && skuProductMap[item.sku]) return skuProductMap[item.sku];
        if (item.ASIN && skuProductMap[item.ASIN]) return skuProductMap[item.ASIN];
        if (item.variant?.skuId && skuProductMap[String(item.variant.skuId)]) return skuProductMap[String(item.variant.skuId)];
        for (const v of (item.variants || [])) {
          if (v.skuId && skuProductMap[String(v.skuId)]) return skuProductMap[String(v.skuId)];
        }
        return null;
      };

      const warnings = {};
      processingOrders.forEach(order => {
        const orderKey = order.upsaleoOrderId || order.orderId;
        const issues = [];
        order.orderDetails?.forEach(item => {
          const product = findProduct(item);
          const sku = item.sku || item.ASIN || "unknown";
          if (!product) {
            issues.push(`SKU ${sku}: Product not found in database`);
          } else {
            if (!product.sellerOpenId || product.sellerOpenId === "unknown") {
              issues.push(`SKU ${sku}: Supplier (sellerOpenId) missing`);
            }
            if (!product.offerId) {
              issues.push(`SKU ${sku}: offerId missing`);
            }
            // Check specId on matching variation
            const variant = item.variant || {};
            const matchedVariation = product.variations?.find(v => String(v.skuId) === String(item.sku)) || product.variations?.[0];
            if (!variant.specId && !matchedVariation?.specId) {
              issues.push(`SKU ${sku}: specId missing`);
            }
          }
        });
        if (issues.length > 0) {
          warnings[orderKey] = issues;
          // Also key by both IDs for lookup
          if (order.orderId) warnings[order.orderId] = issues;
          if (order.upsaleoOrderId) warnings[order.upsaleoOrderId] = issues;
        }
      });
      setOrderWarnings(warnings);
    }).catch(() => {
      // Silently fail — don't block the page
    });
  }, [data]);

  // Same calculation as order details (OrderInfo) so list IMPORTERR PRICE matches details exactly
  const calculateImporterPrice = (order) => {
    if (!Array.isArray(order?.orderDetails) || order.orderDetails.length === 0) {
      const fallback = Number(order?.totalPrice) ?? Number(order?.totalConsumerPrice) ?? 0;
      return Number.isFinite(fallback) ? fallback : 0;
    }

    const wholesaleBreakdownMap = new Map();
    if (Array.isArray(order?.breakdown)) {
      for (const breakdownItem of order.breakdown) {
        if (breakdownItem?.pricingMode === 'wholesale' && breakdownItem?.productId && breakdownItem?.breakdown?.[0]?.FBP) {
          wholesaleBreakdownMap.set(breakdownItem.productId.toString(), Number(breakdownItem.breakdown[0].FBP) || 0);
        }
      }
    }

    const isManualOrder = order?.marketplace?.toLowerCase() === 'manual' || order?.createdVia === 'manual_admin' || order?.createdVia === 'split_admin';
    const orderType = isManualOrder ? (order?.orderType?.toLowerCase()) : null;
    const isOrderWholesale = isManualOrder && orderType === 'wholesale';

    const getProductId = (detail) =>
      detail?.product?._id?.toString() || detail?.product?.toString() || detail?.productId?._id?.toString() || (detail?.productId && typeof detail.productId === 'object' ? String(detail.productId._id || detail.productId) : detail?.productId?.toString()) || '';

    const getPricingMode = (detail) =>
      getOrderItemPricingMode(detail, {
        orderBreakdown: order?.breakdown || [],
        orderType,
        isManualOrder,
        dropshippingLabel: "dropshipping",
      });

    const getDropshippingPrice = (detail) => {
      const variant = detail?.variant;
      if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) return Number(variant.finalPrice);
      return Number(variant?.breakdown?.FBP ?? variant?.breakdown?.fbp) || 0;
    };

    const getWholesalePrice = (detail) => {
      const variant = detail?.variant;
      if (variant?.wholesaleFinalPrice != null && Number(variant.wholesaleFinalPrice) > 0) return Number(variant.wholesaleFinalPrice);
      const productId = getProductId(detail);
      const wholesaleFBP = wholesaleBreakdownMap.get(productId);
      if (wholesaleFBP) return wholesaleFBP;
      return Number(variant?.breakdown?.FBP ?? variant?.breakdown?.fbp) || 0;
    };

    // Group by productId (same as OrderInfo)
    const productGroups = new Map();
    order.orderDetails.forEach((detail) => {
      const productId = getProductId(detail);
      if (!productGroups.has(productId)) productGroups.set(productId, []);
      productGroups.get(productId).push(detail);
    });

    let productTotal = 0;
    productGroups.forEach((details, productId) => {
      let productDropshippingTotal = 0;
      let productWholesaleQuantity = 0;
      let productWholesaleTotal = 0;
      details.forEach((detail) => {
        const pricingMode = getPricingMode(detail);
        const quantity = Number(detail?.quantity) || 0;
        if (pricingMode === 'dropshipping') {
          productDropshippingTotal += getDropshippingPrice(detail) * quantity;
        } else {
          if (isManualOrder && isOrderWholesale) {
            productWholesaleTotal += getWholesalePrice(detail) * quantity;
          }
          productWholesaleQuantity += quantity;
        }
      });
      productTotal += productDropshippingTotal;
      if (isManualOrder && isOrderWholesale && productWholesaleTotal > 0) {
        productTotal += productWholesaleTotal;
      } else {
        const wholesaleFBP = wholesaleBreakdownMap.get(productId);
        if (wholesaleFBP && productWholesaleQuantity > 0) productTotal += wholesaleFBP;
      }
    });

    if (productTotal > 0) return productTotal;
    const fallback = Number(order?.totalPrice) ?? Number(order?.totalConsumerPrice);
    if (Number.isFinite(fallback)) return fallback;
    return order.orderDetails.reduce((acc, d) => acc + (Number(d?.consumerPrice) || 0) * (Number(d?.quantity) || 1), 0);
  };

  /** Gross before IM + final payable (uses stored imPointsDiscount / amountPayableAfterImPoints). */
  const enrichOrderPricingRow = (item) => {
    const calculatedImporterPrice = calculateImporterPrice(item);
    const shippingCharges = Number(item?.totalShippingCharges) || 0;
    // Keep this exactly aligned with Order Details page:
    // Grand Total = importer price (from details/breakdown) + shipping - IM points
    const grossBeforeIm = calculatedImporterPrice + shippingCharges;
    const imPointsDiscount = getImPointsDiscount(item);
    const finalPayableAfterIm =
      item?.amountPayableAfterImPoints != null && Number.isFinite(Number(item.amountPayableAfterImPoints))
        ? Number(item.amountPayableAfterImPoints)
        : Math.max(0, grossBeforeIm - imPointsDiscount);
    return {
      calculatedImporterPrice,
      shippingCharges,
      mkpPrice: Number(item?.mkp_current_total_price) || 0,
      isSplitOrder:
        item?.createdVia === "split_admin" ||
        (Array.isArray(item?.orderStatus) &&
          item.orderStatus.some((s) => s?.status === "Order split")),
      mkpTotalWithShipping: (Number(item?.mkp_current_total_price) || 0) + shippingCharges,
      grossBeforeIm,
      imPointsDiscount,
      finalPayableAfterIm,
      importerPrice: finalPayableAfterIm,
    };
  };

  const tableData = useMemo(
    () =>
      (data || []).map((item, index) => {
        const p = enrichOrderPricingRow(item);
        return {
          ...item,
          serialNumber: (currentPage - 1) * effectivePageSize + index + 1,
          importerPrice: p.importerPrice,
          mkpTotalWithShipping: p.mkpTotalWithShipping,
          shippingCharges: p.shippingCharges,
          imPointsDiscount: p.imPointsDiscount,
          grossBeforeImPoints: p.grossBeforeIm,
          finalPayableAfterIm: p.finalPayableAfterIm,
          indiaSNo: item?.originalExcelData?.['India S.No'] || item?.originalExcelData?.['India S.No.'] || '-',
        };
      }),
    [data, currentPage, effectivePageSize]
  );

  useEffect(() => {
    setSelectedOrders([]);
  }, [data]);

  const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
      return "₹0.00";
    }
    return `₹${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getSellerName = (order) => {
    const seller = order?.sellerId;
    if (!seller) return "-";
    return `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
  };

  const getOfferIdFromDetail = (detail) => {
    const fromVariant = detail?.variant?.offerId;
    const fromProduct = detail?.product?.offerId;
    if (fromVariant != null && fromVariant !== "") return String(fromVariant);
    if (fromProduct != null && fromProduct !== "") return String(fromProduct);
    return null;
  };

  const getUniqueOfferIds = (order) => {
    const details = order?.orderDetails || [];
    const set = new Set();
    details.forEach((d) => {
      const oid = getOfferIdFromDetail(d);
      if (oid) set.add(oid);
    });
    return Array.from(set);
  };

  const getOrderDetailsGroupedByOfferId = (order) => {
    const details = order?.orderDetails || [];
    const groups = {};
    details.forEach((d) => {
      const oid = getOfferIdFromDetail(d) || "_unknown_";
      if (!groups[oid]) groups[oid] = { offerId: oid === "_unknown_" ? null : oid, items: [] };
      groups[oid].items.push(d);
    });
    return Object.values(groups).filter((g) => g.offerId != null);
  };

  const handleSplitClick = (row) => {
    setSplitModal({ open: true, order: null, selectedOfferIds: [], loading: true });
    orderApiService
      .getOrderById(row._id)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setSplitModal((prev) => ({ ...prev, order: res.data, loading: false }));
        } else {
          message.showToast(res?.error?.message || "Failed to load order", ToastTypes.ERROR);
          setSplitModal((prev) => ({ ...prev, loading: false }));
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Failed to load order", ToastTypes.ERROR);
        setSplitModal((prev) => ({ ...prev, loading: false }));
      });
  };

  const handleSplitConfirm = async () => {
    const order = splitModal.order;
    const selected = splitModal.selectedOfferIds || [];
    if (!order?._id || selected.length === 0) {
      message.showToast("Select at least one offer ID to move to the new order", ToastTypes.WARNING);
      return;
    }
    const uniqueInOrder = getUniqueOfferIds(order);
    const remaining = uniqueInOrder.filter((id) => !selected.includes(id));
    if (remaining.length === 0) {
      message.showToast("Leave at least one offer ID in the original order", ToastTypes.WARNING);
      return;
    }
    setSplitSubmitting(true);
    try {
      const res = await orderApiService.splitOrder(order._id, selected);
      setSplitSubmitting(false);
      setSplitModal({ open: false, order: null, selectedOfferIds: [], loading: false });
      if (res?.status?.code === 200) {
        const newId = res?.data?.newOrder?.upsaleoOrderId;
        message.showToast(newId ? `Order split. New order: ${newId}` : "Order split successfully", ToastTypes.SUCCESS);
        if (onRefresh) onRefresh();
      } else {
        message.showToast(res?.error?.message || "Failed to split order", ToastTypes.ERROR);
      }
    } catch (err) {
      setSplitSubmitting(false);
      message.showToast(err?.message || "Failed to split order", ToastTypes.ERROR);
    }
  };

  const handleRowNavigate = (order) => {
    if (!order?._id) return;
    navigate(`/admin/order-management/details/${order._id}`);
  };

  const handleSelectionChange = (selected) => {
    setSelectedOrders(selected);
  };

  const handleFile = (e) => {
    setUploadFile(e.target.files[0]);
  };

  const handleTracking = () => {
    if (!uploadFile) {
      message.showToast("Please choose a file to upload", ToastTypes.ERROR);
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);

    loader.showLoading(true);
    orderApiService
      .addExcelTracking(formData)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast(res.status.message, ToastTypes.SUCCESS);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  const handleExportCsv = () => {
    const exportData =
      selectedOrders.length > 0 ? selectedOrders : tableData;

    if (!exportData.length) {
      message.showToast("No orders to export", ToastTypes.WARNING);
      return;
    }

    const csvRows = exportData.map((item, index) => {
      const baseRow = {
        "S.No.": item?.serialNumber ?? index + 1,
        "Importerr Order Id": item?.upsaleoOrderId || "",
        Buyer: getSellerName(item),
        "MKP Name": item?.marketplace?.toLowerCase() === "manual" ? "MANUAL" : (item?.marketplace || ""),
        "Customer Email": item?.customerEmail || "",
        "Order ID": item?.orderId || item?.orderItemId || "",
        "Order Date": item?.orderDate
          ? moment(item.orderDate).format("DD/MM/YYYY")
          : "",
        Status: item?.status || "",
        "From Failed Order": item?.fromFailedOrder ? "Yes" : "No",
        "MKP Payment Status": (() => {
          // Get mkp_financial_status from last item in mkpOrderStatusHistory array
          if (Array.isArray(item?.mkpOrderStatusHistory) && item.mkpOrderStatusHistory.length > 0) {
            const lastItem = item.mkpOrderStatusHistory[item.mkpOrderStatusHistory.length - 1];
            return lastItem?.mkp_order_status || item?.mkp_financial_status || "";
          }
          return item?.mkp_financial_status || "";
        })(),
        "Importerr Price": item?.importerPrice || 0,
        "IM Points Discount": item?.imPointsDiscount ?? 0,
        "Order Total Before IM": item?.grossBeforeImPoints ?? 0,
        "MKP Total Price": item?.mkpTotalWithShipping || 0,
        "Shipping Charges": item?.shippingCharges || 0,
        State: item?.shippingState || "",
        "Updated Date": item?.updatedAt
          ? moment(item.updatedAt).format("DD/MM/YYYY")
          : "",
      };

      // Only include India S.No for mapped orders tab
      if (activeTab === 'mapped') {
        baseRow["India S.No"] = item?.originalExcelData?.['India S.No'] || item?.originalExcelData?.['India S.No.'] || "-";
      }

      return baseRow;
    });


    exportToCSV(csvRows, `orders_${Date.now()}`);
  };

  const handleExportToSheet = async () => {
    const exportData = tableData;

    if (!exportData.length) {
      message.showToast("No orders to export", ToastTypes.WARNING);
      return;
    }

    loader.showInlineLoader("Fetching full order details and product data...", "medium");

    try {
      const fullOrders = await Promise.all(
        exportData.map(async (order) => {
          if (!order?._id) return order;
          try {
            const res = await orderApiService.getOrderById(order._id);
            if (res?.status?.code === 200 && res?.data) {
              const merged = { ...order, ...res.data };
              // getOrderById does not populate sellerId; keep populated seller from list so export shows seller name
              const sellerPopulated = order?.sellerId && typeof order.sellerId === "object" && (order.sellerId.firstName != null || order.sellerId.lastName != null || order.sellerId.email);
              if (sellerPopulated) merged.sellerId = order.sellerId;
              // Preserve customerEmail and createdAt from list if full order response doesn't have them
              if (order.customerEmail && !merged.customerEmail) merged.customerEmail = order.customerEmail;
              if (order.createdAt && !merged.createdAt) merged.createdAt = order.createdAt;
              return merged;
            }
          } catch (_) { }
          return order;
        })
      );

      const allSkus = [
        ...new Set(
          fullOrders.flatMap((order) => {
            const skus = [];
            order.orderDetails?.forEach((item) => {
              if (item.sku) skus.push(String(item.sku));
              if (item.ASIN) skus.push(String(item.ASIN));
              if (item.product?.sku) skus.push(String(item.product.sku));
              if (item.variant?.skuId) skus.push(String(item.variant.skuId));
              item.variants?.forEach((v) => {
                if (v.skuId) skus.push(String(v.skuId));
              });
            });
            return skus;
          })
        ),
      ].filter(Boolean);

      let productMap = {};
      if (allSkus.length > 0) {
        const productRes = await productApiService.getProductsBySkus(allSkus);
        const products = productRes?.data?.products || [];
        products.forEach((p) => {
          const imgUrl = p.imgUrl?.[0] || p.image || "";
          const sku = p.sku || p.offerId || "";
          const title = p.title || "";
          const totalStock = p.variations?.reduce((sum, v) => sum + (Number(v.amountOnSale) || 0), 0) ?? "";
          const variantStocks = {};
          p.variations?.forEach((v) => {
            if (v.skuId != null) variantStocks[String(v.skuId)] = v.amountOnSale != null ? Number(v.amountOnSale) : "";
          });
          const entry = { imgUrl, sku, title, stock: totalStock, variantStocks };
          if (p.sku) productMap[p.sku] = entry;
          if (p.offerId) productMap[String(p.offerId)] = entry;
          p.variations?.forEach((v) => {
            if (v.skuId) productMap[String(v.skuId)] = entry;
            v.mappedAsins?.forEach((asin) => {
              if (asin) productMap[asin] = entry;
            });
          });
          p.amazonMappings?.forEach((m) => {
            if (m.asin) productMap[m.asin] = entry;
          });
        });
      }

      // Fetch seller emails from User collection
      const uniqueSellerIds = [
        ...new Set(
          fullOrders
            .map((order) => {
              const sid = order?.sellerId;
              if (!sid) return null;
              // If already populated object, get _id; otherwise use as-is
              return typeof sid === "object" ? (sid._id || sid.id || null) : sid;
            })
            .filter(Boolean)
            .map(String)
        ),
      ];

      let sellerMap = {};
      if (uniqueSellerIds.length > 0) {
        const sellerResults = await Promise.all(
          uniqueSellerIds.map(async (sellerId) => {
            try {
              const res = await userApiService.getUserById(sellerId);
              if (res?.status?.code === 200 && res?.data) {
                return { sellerId, user: res.data };
              }
            } catch (_) { }
            return { sellerId, user: null };
          })
        );
        sellerResults.forEach(({ sellerId, user }) => {
          if (user) {
            sellerMap[sellerId] = {
              email: user.email || "",
              firstName: user.firstName || "",
              lastName: user.lastName || "",
              uniqueId: user.uniqueId || "",
            };
          }
        });
      }

      const result = await exportOrdersToSheetWithItems(fullOrders, {
        getSellerName,
        sellerMap,
        getMkpPaymentStatus: (order) => {
          if (Array.isArray(order?.mkpOrderStatusHistory) && order.mkpOrderStatusHistory.length > 0) {
            const lastItem = order.mkpOrderStatusHistory[order.mkpOrderStatusHistory.length - 1];
            return lastItem?.mkp_order_status || order?.mkp_financial_status || "";
          }
          return order?.mkp_financial_status || "";
        },
        productMap,
      });

      loader.hideLoader();

      if (result.success) {
        message.showToast(`Exported ${result.rowCount ?? fullOrders.length} rows to sheet`, ToastTypes.SUCCESS);
      } else {
        message.showToast(result.error || "Export failed", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Export failed", ToastTypes.ERROR);
    }
  };

  const handleExportAllToSheet = async () => {
    if (!onFetchAllOrders) {
      message.showToast("Export all data is not available", ToastTypes.WARNING);
      return;
    }
    loader.showInlineLoader("Fetching all orders...", "medium");
    let allOrders = [];
    try {
      allOrders = await onFetchAllOrders();
      loader.hideLoader();
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Failed to fetch orders", ToastTypes.ERROR);
      return;
    }
    if (!allOrders || !allOrders.length) {
      message.showToast("No orders to export", ToastTypes.WARNING);
      return;
    }
    const exportData = allOrders.map((item, index) => {
      const p = enrichOrderPricingRow(item);
      return {
        ...item,
        serialNumber: index + 1,
        importerPrice: p.importerPrice,
        mkpTotalWithShipping: p.mkpTotalWithShipping,
        shippingCharges: p.shippingCharges,
        imPointsDiscount: p.imPointsDiscount,
        grossBeforeImPoints: p.grossBeforeIm,
        finalPayableAfterIm: p.finalPayableAfterIm,
        indiaSNo: item?.originalExcelData?.['India S.No'] || item?.originalExcelData?.['India S.No.'] || '-',
      };
    });
    loader.showInlineLoader("Fetching full order details and product data...", "medium");
    try {
      const fullOrders = await Promise.all(
        exportData.map(async (order) => {
          if (!order?._id) return order;
          try {
            const res = await orderApiService.getOrderById(order._id);
            if (res?.status?.code === 200 && res?.data) {
              const merged = { ...order, ...res.data };
              const sellerPopulated = order?.sellerId && typeof order.sellerId === "object" && (order.sellerId.firstName != null || order.sellerId.lastName != null || order.sellerId.email);
              if (sellerPopulated) merged.sellerId = order.sellerId;
              if (order.customerEmail && !merged.customerEmail) merged.customerEmail = order.customerEmail;
              if (order.createdAt && !merged.createdAt) merged.createdAt = order.createdAt;
              merged.importerPrice = order.importerPrice;
              merged.mkpTotalWithShipping = order.mkpTotalWithShipping;
              merged.shippingCharges = order.shippingCharges;
              return merged;
            }
          } catch (_) { }
          return order;
        })
      );
      const allSkus = [
        ...new Set(
          fullOrders.flatMap((order) => {
            const skus = [];
            order.orderDetails?.forEach((item) => {
              if (item.sku) skus.push(String(item.sku));
              if (item.ASIN) skus.push(String(item.ASIN));
              if (item.product?.sku) skus.push(String(item.product.sku));
              if (item.variant?.skuId) skus.push(String(item.variant.skuId));
              item.variants?.forEach((v) => {
                if (v.skuId) skus.push(String(v.skuId));
              });
            });
            return skus;
          })
        ),
      ].filter(Boolean);
      let productMap = {};
      if (allSkus.length > 0) {
        const productRes = await productApiService.getProductsBySkus(allSkus);
        const products = productRes?.data?.products || [];
        products.forEach((p) => {
          const imgUrl = p.imgUrl?.[0] || p.image || "";
          const sku = p.sku || p.offerId || "";
          const title = p.title || "";
          const totalStock = p.variations?.reduce((sum, v) => sum + (Number(v.amountOnSale) || 0), 0) ?? "";
          const variantStocks = {};
          p.variations?.forEach((v) => {
            if (v.skuId != null) variantStocks[String(v.skuId)] = v.amountOnSale != null ? Number(v.amountOnSale) : "";
          });
          const entry = { imgUrl, sku, title, stock: totalStock, variantStocks };
          if (p.sku) productMap[p.sku] = entry;
          if (p.offerId) productMap[String(p.offerId)] = entry;
          p.variations?.forEach((v) => {
            if (v.skuId) productMap[String(v.skuId)] = entry;
            v.mappedAsins?.forEach((asin) => {
              if (asin) productMap[asin] = entry;
            });
          });
          p.amazonMappings?.forEach((m) => {
            if (m.asin) productMap[m.asin] = entry;
          });
        });
      }
      const uniqueSellerIds = [
        ...new Set(
          fullOrders
            .map((order) => {
              const sid = order?.sellerId;
              if (!sid) return null;
              return typeof sid === "object" ? (sid._id || sid.id || null) : sid;
            })
            .filter(Boolean)
            .map(String)
        ),
      ];
      let sellerMap = {};
      if (uniqueSellerIds.length > 0) {
        const sellerResults = await Promise.all(
          uniqueSellerIds.map(async (sellerId) => {
            try {
              const res = await userApiService.getUserById(sellerId);
              if (res?.status?.code === 200 && res?.data) {
                return { sellerId, user: res.data };
              }
            } catch (_) { }
            return { sellerId, user: null };
          })
        );
        sellerResults.forEach(({ sellerId, user }) => {
          if (user) {
            sellerMap[sellerId] = {
              email: user.email || "",
              firstName: user.firstName || "",
              lastName: user.lastName || "",
              uniqueId: user.uniqueId || "",
            };
          }
        });
      }
      const result = await exportOrdersToSheetWithItems(fullOrders, {
        getSellerName,
        sellerMap,
        getMkpPaymentStatus: (order) => {
          if (Array.isArray(order?.mkpOrderStatusHistory) && order.mkpOrderStatusHistory.length > 0) {
            const lastItem = order.mkpOrderStatusHistory[order.mkpOrderStatusHistory.length - 1];
            return lastItem?.mkp_order_status || order?.mkp_financial_status || "";
          }
          return order?.mkp_financial_status || "";
        },
        productMap,
      });
      loader.hideLoader();
      if (result.success) {
        message.showToast(`Exported ${result.rowCount ?? fullOrders.length} rows to sheet`, ToastTypes.SUCCESS);
      } else {
        message.showToast(result.error || "Export failed", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Export failed", ToastTypes.ERROR);
    }
  };

  const handleProcessExportCsv = () => {
    orderApiService.getprocessingOrder().then((res) => {
      if (res.status.code === 200) {
        const url = res.data.url;
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "products.csv");
        link.click();
      } else {
        // Handle error case
      }
    });
  };

  const handleExportDateWise = async () => {
    if (!exportDateRange.startDate || !exportDateRange.endDate) {
      message.showToast("Please select start date and end date", ToastTypes.WARNING);
      return;
    }

    setExporting(true);
    try {
      // Get current filter values from parent component
      const params = {
        startDate: exportDateRange.startDate,
        endDate: exportDateRange.endDate,
        search: '', // You may want to pass this from props if available
        status: '', // You may want to pass this from props if available
        format: 'json' // Add format parameter to request JSON instead of CSV
      };

      const res = await orderApiService.exportOrdersDateWise(params);

      if (res.status.code === 200) {
        if (res.data?.orders && Array.isArray(res.data.orders)) {
          // Process the JSON data and download it
          const jsonData = res.data.orders.map((order, index) => {
            const p = enrichOrderPricingRow(order);

            // Get seller name
            const getSellerName = (order) => {
              const seller = order?.sellerId;
              if (!seller) return "-";
              return `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
            };

            // Get MKP payment status
            const getMkpPaymentStatus = (order) => {
              if (Array.isArray(order?.mkpOrderStatusHistory) && order.mkpOrderStatusHistory.length > 0) {
                const lastItem = order.mkpOrderStatusHistory[order.mkpOrderStatusHistory.length - 1];
                return lastItem?.mkp_order_status || order?.mkp_financial_status || "";
              }
              return order?.mkp_financial_status || "";
            };

            return {
              ...order,
              "S.No.": index + 1,
              "Buyer": getSellerName(order),
              "MKP Name": order?.marketplace?.toLowerCase() === "manual" ? "MANUAL" : (order?.marketplace || ""),
              "Order Date": order?.orderDate ? moment(order.orderDate).format("DD/MM/YYYY") : "",
              "From Failed Order": order?.fromFailedOrder ? "Yes" : "No",
              "MKP Payment Status": getMkpPaymentStatus(order),
              "Importerr Price": p.importerPrice || 0,
              "MKP Total Price": p.mkpTotalWithShipping || 0,
              "Shipping Charges": p.shippingCharges || 0,
              "IM Points Discount": p.imPointsDiscount || 0,
              "Order Total Before IM": p.grossBeforeIm || 0,
              "Updated Date": order?.updatedAt ? moment(order.updatedAt).format("DD/MM/YYYY") : ""
            };
          });

          // Download CSV file
          const headers = [
            "S.No.",
            "_id",
            "Order ID",
            "Importerr Order Id",
            "Customer Email",
            "Buyer",
            "MKP Name",
            "Status",
            "Order Date",
            "Updated Date",
            "Created Date",
            "Paid Date",
            "From Failed Order",
            "MKP Payment Status",
            "Importerr Price",
            "IM Points Discount",
            "Order Total Before IM",
            "MKP Total Price",
            "Shipping Charges",
            "Total Quantity",
            "Total Price",
            "Cross Border Status",
            "Shipping Method",
            "Warehouse ID",
            "Warehouse Name",
            "Warehouse Address 1",
            "Warehouse Address 2",
            "Warehouse City",
            "Warehouse State",
            "Warehouse Pincode",
            "Warehouse Country",
            "Warehouse Phone",
            "Warehouse Landmark",
            "Warehouse Instructions",
            "GST On Shipping",
            "Use Same Address For Billing",
            "Customer Note",
            "Marketplace",
            "Amazon Tracking Synced",
            "Ticket",
            "Order Export",
            "MKP Current Total Price",
            "MKP Financial Status",
            "MKP Current Total Tax",
            "MKP Current Subtotal Price",
            "MKP Current Received Price",
            "Transaction ID",
            "Total Quantity",
            "Total Price",
            "__v"
          ];

          const csvContent = [
            headers.join(','),
            ...jsonData.map(row => [
              row["S.No."],
              `"${row["_id"] || ""}"`,
              `"${row["Order ID"] || row["orderId"] || ""}"`,
              `"${row["Importerr Order Id"] || row["upsaleoOrderId"] || ""}"`,
              `"${row["Customer Email"] || row["customerEmail"] || ""}"`,
              `"${row["Buyer"]}"`,
              `"${row["MKP Name"]}"`,
              `"${row["Status"] || row["status"] || ""}"`,
              `"${row["Order Date"]}"`,
              `"${row["Updated Date"]}"`,
              `"${row["Created Date"] || (row["createdAt"] ? moment(row.createdAt).format("DD/MM/YYYY") : "")}"`,
              `"${row["Paid Date"] || (row["paidDate"] ? moment(row.paidDate).format("DD/MM/YYYY") : "")}"`,
              `"${row["From Failed Order"]}"`,
              `"${row["MKP Payment Status"]}"`,
              row["Importerr Price"],
              row["IM Points Discount"],
              row["Order Total Before IM"],
              row["MKP Total Price"],
              row["Shipping Charges"],
              `"${row["Total Quantity"] || row["totalQuantity"] || ""}"`,
              `"${row["Total Price"] || row["totalPrice"] || ""}"`,
              `"${row["Cross Border Status"] || row["crossBorderStatus"] || ""}"`,
              `"${row["Shipping Method"] || row["shippingMethod"] || ""}"`,
              `"${row["Warehouse ID"] || row["warehouseId"] || ""}"`,
              `"${row["Warehouse Name"] || row["warehouseName"] || ""}"`,
              `"${row["Warehouse Address 1"] || row["warehouseAddress1"] || ""}"`,
              `"${row["Warehouse Address 2"] || row["warehouseAddress2"] || ""}"`,
              `"${row["Warehouse City"] || row["warehouseCity"] || ""}"`,
              `"${row["Warehouse State"] || row["warehouseState"] || ""}"`,
              `"${row["Warehouse Pincode"] || row["warehousePincode"] || ""}"`,
              `"${row["Warehouse Country"] || row["warehouseCountry"] || ""}"`,
              `"${row["Warehouse Phone"] || row["warehousePhone"] || ""}"`,
              `"${row["Warehouse Landmark"] || row["warehouseLandmark"] || ""}"`,
              `"${row["Warehouse Instructions"] || row["warehouseInstructions"] || ""}"`,
              `"${row["GST On Shipping"] || row["gstOnShipping"] || ""}"`,
              `"${row["Use Same Address For Billing"] || row["useSameAddressForBilling"] || ""}"`,
              `"${row["Customer Note"] || row["customerNote"] || ""}"`,
              `"${row["Marketplace"] || row["marketplace"] || ""}"`,
              `"${row["Amazon Tracking Synced"] || row["amazonTrackingSynced"] || ""}"`,
              `"${row["Ticket"] || row["ticket"] || ""}"`,
              `"${row["Order Export"] || row["orderExport"] || ""}"`,
              `"${row["MKP Current Total Price"] || row["mkp_current_total_price"] || ""}"`,
              `"${row["MKP Financial Status"] || row["mkp_financial_status"] || ""}"`,
              `"${row["MKP Current Total Tax"] || row["mkp_current_total_tax"] || ""}"`,
              `"${row["MKP Current Subtotal Price"] || row["mkp_current_subtotal_price"] || ""}"`,
              `"${row["MKP Current Received Price"] || row["mkp_current_received_price"] || ""}"`,
              `"${row["Transaction ID"] || row["transactionId"] || ""}"`,
              `"${row["Total Quantity"] || row["totalQuantity"] || ""}"`,
              `"${row["Total Price"] || row["totalPrice"] || ""}"`,
              `"${row["__v"] || ""}"`
            ].join(','))
          ].join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `orders_export_${exportDateRange.startDate}_to_${exportDateRange.endDate}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          message.showToast(`Exported ${jsonData.length} orders successfully`, ToastTypes.SUCCESS);
        } else {
          message.showToast("Export completed but no data available", ToastTypes.WARNING);
        }
      } else {
        message.showToast(res.error?.message || "Export failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Export failed", ToastTypes.ERROR);
    } finally {
      setExporting(false);
    }
  };

  const handleExportAllOrders = async () => {
    setExporting(true);
    try {
      // Get current filter values from parent component
      const params = {
        search: '', // You may want to pass this from props if available
        status: '', // You may want to pass this from props if available
        format: 'json' // Add format parameter to request JSON instead of CSV
      };

      const res = await orderApiService.exportAllOrders(params);

      if (res.status.code === 200) {
        if (res.data?.orders && Array.isArray(res.data.orders)) {
          // Process JSON data and download it
          const jsonData = res.data.orders.map((order, index) => {
            const p = enrichOrderPricingRow(order);

            // Get seller name
            const getSellerName = (order) => {
              const seller = order?.sellerId;
              if (!seller) return "-";
              return `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
            };

            // Get MKP payment status
            const getMkpPaymentStatus = (order) => {
              if (Array.isArray(order?.mkpOrderStatusHistory) && order.mkpOrderStatusHistory.length > 0) {
                const lastItem = order.mkpOrderStatusHistory[order.mkpOrderStatusHistory.length - 1];
                return lastItem?.mkp_order_status || order?.mkp_financial_status || "";
              }
              return order?.mkp_financial_status || "";
            };

            return {
              ...order,
              "S.No.": index + 1,
              "Buyer": getSellerName(order),
              "MKP Name": order?.marketplace?.toLowerCase() === "manual" ? "MANUAL" : (order?.marketplace || ""),
              "Order Date": order?.orderDate ? moment(order.orderDate).format("DD/MM/YYYY") : "",
              "From Failed Order": order?.fromFailedOrder ? "Yes" : "No",
              "MKP Payment Status": getMkpPaymentStatus(order),
              "Importerr Price": p.importerPrice || 0,
              "MKP Total Price": p.mkpTotalWithShipping || 0,
              "Shipping Charges": p.shippingCharges || 0,
              "IM Points Discount": p.imPointsDiscount || 0,
              "Order Total Before IM": p.grossBeforeIm || 0,
              "Updated Date": order?.updatedAt ? moment(order.updatedAt).format("DD/MM/YYYY") : ""
            };
          });

          // Download CSV file
          const headers = [
            "S.No.",
            "_id",
            "Order ID",
            "Importerr Order Id",
            "Customer Email",
            "Buyer",
            "MKP Name",
            "Status",
            "Order Date",
            "Updated Date",
            "Created Date",
            "Paid Date",
            "From Failed Order",
            "MKP Payment Status",
            "Importerr Price",
            "IM Points Discount",
            "Order Total Before IM",
            "MKP Total Price",
            "Shipping Charges",
            "Total Quantity",
            "Total Price",
            "Cross Border Status",
            "Shipping Method",
            "Warehouse ID",
            "Warehouse Name",
            "Warehouse Address 1",
            "Warehouse Address 2",
            "Warehouse City",
            "Warehouse State",
            "Warehouse Pincode",
            "Warehouse Country",
            "Warehouse Phone",
            "Warehouse Landmark",
            "Warehouse Instructions",
            "GST On Shipping",
            "Use Same Address For Billing",
            "Customer Note",
            "Marketplace",
            "Amazon Tracking Synced",
            "Ticket",
            "Order Export",
            "MKP Current Total Price",
            "MKP Financial Status",
            "MKP Current Total Tax",
            "MKP Current Subtotal Price",
            "MKP Current Received Price",
            "Transaction ID",
            "Total Quantity",
            "Total Price",
            "__v"
          ];

          const csvContent = [
            headers.join(','),
            ...jsonData.map(row => [
              row["S.No."],
              `"${row["_id"] || ""}"`,
              `"${row["Order ID"] || row["orderId"] || ""}"`,
              `"${row["Importerr Order Id"] || row["upsaleoOrderId"] || ""}"`,
              `"${row["Customer Email"] || row["customerEmail"] || ""}"`,
              `"${row["Buyer"]}"`,
              `"${row["MKP Name"]}"`,
              `"${row["Status"] || row["status"] || ""}"`,
              `"${row["Order Date"]}"`,
              `"${row["Updated Date"]}"`,
              `"${row["Created Date"] || (row["createdAt"] ? moment(row.createdAt).format("DD/MM/YYYY") : "")}"`,
              `"${row["Paid Date"] || (row["paidDate"] ? moment(row.paidDate).format("DD/MM/YYYY") : "")}"`,
              `"${row["From Failed Order"]}"`,
              `"${row["MKP Payment Status"]}"`,
              row["Importerr Price"],
              row["IM Points Discount"],
              row["Order Total Before IM"],
              row["MKP Total Price"],
              row["Shipping Charges"],
              `"${row["Total Quantity"] || row["totalQuantity"] || ""}"`,
              `"${row["Total Price"] || row["totalPrice"] || ""}"`,
              `"${row["Cross Border Status"] || row["crossBorderStatus"] || ""}"`,
              `"${row["Shipping Method"] || row["shippingMethod"] || ""}"`,
              `"${row["Warehouse ID"] || row["warehouseId"] || ""}"`,
              `"${row["Warehouse Name"] || row["warehouseName"] || ""}"`,
              `"${row["Warehouse Address 1"] || row["warehouseAddress1"] || ""}"`,
              `"${row["Warehouse Address 2"] || row["warehouseAddress2"] || ""}"`,
              `"${row["Warehouse City"] || row["warehouseCity"] || ""}"`,
              `"${row["Warehouse State"] || row["warehouseState"] || ""}"`,
              `"${row["Warehouse Pincode"] || row["warehousePincode"] || ""}"`,
              `"${row["Warehouse Country"] || row["warehouseCountry"] || ""}"`,
              `"${row["Warehouse Phone"] || row["warehousePhone"] || ""}"`,
              `"${row["Warehouse Landmark"] || row["warehouseLandmark"] || ""}"`,
              `"${row["Warehouse Instructions"] || row["warehouseInstructions"] || ""}"`,
              `"${row["GST On Shipping"] || row["gstOnShipping"] || ""}"`,
              `"${row["Use Same Address For Billing"] || row["useSameAddressForBilling"] || ""}"`,
              `"${row["Customer Note"] || row["customerNote"] || ""}"`,
              `"${row["Marketplace"] || row["marketplace"] || ""}"`,
              `"${row["Amazon Tracking Synced"] || row["amazonTrackingSynced"] || ""}"`,
              `"${row["Ticket"] || row["ticket"] || ""}"`,
              `"${row["Order Export"] || row["orderExport"] || ""}"`,
              `"${row["MKP Current Total Price"] || row["mkp_current_total_price"] || ""}"`,
              `"${row["MKP Financial Status"] || row["mkp_financial_status"] || ""}"`,
              `"${row["MKP Current Total Tax"] || row["mkp_current_total_tax"] || ""}"`,
              `"${row["MKP Current Subtotal Price"] || row["mkp_current_subtotal_price"] || ""}"`,
              `"${row["MKP Current Received Price"] || row["mkp_current_received_price"] || ""}"`,
              `"${row["Transaction ID"] || row["transactionId"] || ""}"`,
              `"${row["Total Quantity"] || row["totalQuantity"] || ""}"`,
              `"${row["Total Price"] || row["totalPrice"] || ""}"`,
              `"${row["__v"] || ""}"`
            ].join(','))
          ].join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "orders_all_export.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          message.showToast(`Exported ${jsonData.length} orders successfully`, ToastTypes.SUCCESS);
        } else {
          message.showToast("Export completed but no data available", ToastTypes.WARNING);
        }
      } else {
        message.showToast(res.error?.message || "Export failed", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Export failed", ToastTypes.ERROR);
    } finally {
      setExporting(false);
    }
  };

  // Process selected orders to Cross-Border Orders
  // Open payment confirmation modal
  const handleBulkPayNow = async () => {
    // Automatically filter only payment pending orders from selection
    const pendingOrders = selectedOrders.filter(order => order.status === "payment pending");

    if (pendingOrders.length === 0) {
      message.showToast("No payment pending orders selected", ToastTypes.WARNING);
      return;
    }

    // Calculate payment details for each seller
    const sellerPayments = {};
    let grandTotal = 0;
    const uniqueSellerIds = new Set();

    pendingOrders.forEach(order => {
      const sellerId = order.sellerId?._id || order.sellerId;
      const sellerName = getSellerName(order);
      const orderTotal = order.importerPrice || 0;

      uniqueSellerIds.add(sellerId);

      if (!sellerPayments[sellerId]) {
        sellerPayments[sellerId] = {
          sellerId,
          sellerName,
          sellerEmail: order.sellerId?.email || '-',
          orders: [],
          total: 0,
          walletBalance: 0 // Will be fetched
        };
      }

      sellerPayments[sellerId].orders.push({
        upsaleoOrderId: order.upsaleoOrderId,
        orderId: order.orderId,
        amount: orderTotal
      });
      sellerPayments[sellerId].total += orderTotal;
      grandTotal += orderTotal;
    });

    // Fetch wallet balances for all sellers
    try {
      const walletPromises = Array.from(uniqueSellerIds).map(async (sellerId) => {
        try {
          const walletRes = await userApiService.getWalletByUserId(sellerId);
          if (walletRes?.status?.code === 200 && walletRes?.data) {
            // Now balance is directly in data object
            const balance = walletRes.data.balance || 0;
            return { sellerId, balance };
          }
        } catch (err) {
          console.error(`Failed to fetch wallet for seller ${sellerId}:`, err);
        }
        return { sellerId, balance: 0 };
      });

      const walletResults = await Promise.all(walletPromises);

      // Update seller payments with wallet balances
      walletResults.forEach(({ sellerId, balance }) => {
        if (sellerPayments[sellerId]) {
          sellerPayments[sellerId].walletBalance = balance;
        }
      });
    } catch (err) {
      console.error('Error fetching wallet balances:', err);
    }

    // Calculate how many orders were filtered out
    const filteredOutCount = selectedOrders.length - pendingOrders.length;

    // Set payment confirmation data and show modal
    setPaymentConfirmData({
      pendingOrders,
      sellerPayments: Object.values(sellerPayments),
      grandTotal,
      orderCount: pendingOrders.length,
      sellerCount: Object.keys(sellerPayments).length,
      totalSelected: selectedOrders.length,
      filteredOutCount: filteredOutCount
    });
    setShowPaymentConfirmModal(true);
  };

  // Process payment after confirmation
  const handleConfirmPayment = async () => {
    if (!paymentConfirmData) return;

    setShowPaymentConfirmModal(false);
    setIsProcessingPayment(true);
    setPaymentProgress(0);
    const pendingOrders = paymentConfirmData.pendingOrders;

    loader.showInlineLoader(`Processing payments for ${pendingOrders.length} orders...`, "medium");

    try {
      const orderIds = pendingOrders.map(order => order._id);

      // Simulate progress based on expected time
      const totalOrders = orderIds.length;
      const estimatedTimePerOrder = 200; // ms per order
      const totalEstimatedTime = totalOrders * estimatedTimePerOrder;
      const progressInterval = 100; // Update every 100ms

      // Start progress animation
      let currentProgress = 0;
      const progressTimer = setInterval(() => {
        currentProgress += (progressInterval / totalEstimatedTime) * 90; // Go up to 90%
        if (currentProgress >= 90) {
          clearInterval(progressTimer);
          setPaymentProgress(90); // Cap at 90% until API responds
        } else {
          setPaymentProgress(Math.min(currentProgress, 90));
        }
      }, progressInterval);

      const res = await orderApiService.adminBulkPayment(orderIds);

      // Complete progress to 100%
      clearInterval(progressTimer);
      setPaymentProgress(100);

      // Small delay to show 100% completion
      await new Promise(resolve => setTimeout(resolve, 300));

      loader.hideLoader();
      setIsProcessingPayment(false);
      setPaymentProgress(0);

      if (res.status.code === 200 && res.data) {
        const { successCount, failedCount, skippedCount, totalAmount, results } = res.data;

        // Reset show all states
        setShowAllSuccessOrders(false);
        setShowAllFailedOrders(false);

        // Show success modal with detailed results
        setPaymentSuccessData({
          successCount: successCount || 0,
          failedCount: failedCount || 0,
          skippedCount: skippedCount || 0,
          totalAmount: totalAmount || 0,
          results: results || { success: [], failed: [], skipped: [] }
        });
        setShowPaymentSuccessModal(true);

        // Log for debugging
        console.log('[Bulk Payment Results]', results);

        // Show appropriate toast message
        if (successCount > 0 && failedCount > 0) {
          message.showToast(`${successCount} payments processed. ${failedCount} failed.`, ToastTypes.WARNING);
        } else if (successCount > 0) {
          message.showToast(`Successfully processed ${successCount} payment(s)`, ToastTypes.SUCCESS);
        } else if (failedCount > 0) {
          message.showToast(`All ${failedCount} payment(s) failed. Check details.`, ToastTypes.ERROR);
        }
      } else {
        const errorMsg = res.error?.message || res.status?.message || "Failed to process payments";
        message.showToast(errorMsg, ToastTypes.ERROR);

        // If there are results, show them
        if (res.data?.results) {
          console.error('[Bulk Payment Failed]', res.data.results);
        }
      }
    } catch (err) {
      loader.hideLoader();
      setIsProcessingPayment(false);
      setPaymentProgress(0);
      console.error('[Bulk Payment Error]', err);
      message.showToast(err?.message || "Failed to process bulk payment", ToastTypes.ERROR);
    }
  };

  const handleProcessToCrossBorder = async () => {
    if (selectedOrders.length === 0) {
      message.showToast("Please select orders to process", ToastTypes.WARNING);
      return;
    }

    // Filter only processing status orders
    const processingOrders = selectedOrders.filter(order => order.status === "processing");
    if (processingOrders.length === 0) {
      message.showToast("Please select orders with 'processing' status only", ToastTypes.WARNING);
      return;
    }

    setIsProcessingCrossBorder(true);
    loader.showInlineLoader("Preparing cross-border orders...", "medium");

    try {
      // Get all unique SKUs from selected orders - include item.sku, variant.skuId, and ASIN
      const allSkus = [...new Set(processingOrders.flatMap(order => {
        const skus = [];
        order.orderDetails?.forEach(item => {
          if (item.sku) skus.push(item.sku);
          if (item.ASIN) skus.push(item.ASIN); // Also include ASIN
          // Also collect variant skuIds from order
          item.variants?.forEach(v => {
            if (v.skuId) skus.push(String(v.skuId));
          });
          // Also check variant.skuId in singular variant object
          if (item.variant?.skuId) skus.push(String(item.variant.skuId));
        });
        return skus;
      }))].filter(Boolean);

      console.log("🔍 Cross-border: Fetching products for SKUs:", allSkus);

      // Fetch product info for supplier data
      const productRes = await productApiService.getProductsBySkus(allSkus);
      const products = productRes?.data?.products || [];

      console.log("📦 Cross-border: Found products:", products.length, products.map(p => ({ sku: p.sku, offerId: p.offerId, sellerOpenId: p.sellerOpenId })));

      // Create SKU to product map - map by multiple keys for flexible lookup
      const skuProductMap = {};
      products.forEach(product => {
        if (product.sku) skuProductMap[product.sku] = product;
        if (product.offerId) skuProductMap[String(product.offerId)] = product;
        product.variations?.forEach(v => {
          if (v.skuId) skuProductMap[String(v.skuId)] = product;
          // Also map by variation's mappedAsins
          v.mappedAsins?.forEach(asin => {
            if (asin) skuProductMap[asin] = product;
          });
        });
        // Also map by product-level amazonMappings
        product.amazonMappings?.forEach(mapping => {
          if (mapping.asin) skuProductMap[mapping.asin] = product;
        });
      });

      // Helper function to find product info by trying multiple keys
      const findProductInfo = (item) => {
        // Try item.sku first
        if (item.sku && skuProductMap[item.sku]) {
          return skuProductMap[item.sku];
        }
        // Try ASIN field
        if (item.ASIN && skuProductMap[item.ASIN]) {
          return skuProductMap[item.ASIN];
        }
        // Try variant.skuId from order
        if (item.variant?.skuId && skuProductMap[String(item.variant.skuId)]) {
          return skuProductMap[String(item.variant.skuId)];
        }
        // Try variants array
        for (const v of (item.variants || [])) {
          if (v.skuId && skuProductMap[String(v.skuId)]) {
            return skuProductMap[String(v.skuId)];
          }
        }
        return null;
      };

      // Group orders by supplier
      const supplierOrderMap = {};

      processingOrders.forEach(order => {
        order.orderDetails?.forEach(item => {
          const productInfo = findProductInfo(item) || {};
          const supplierId = productInfo.sellerOpenId || "unknown";
          const orderVariants = item.variants || [];

          console.log(`📋 Order ${order.upsaleoOrderId} item SKU: ${item.sku}, Found product: ${productInfo.sku || 'NOT FOUND'}, Supplier: ${supplierId}`);

          // Initialize supplier group
          if (!supplierOrderMap[supplierId]) {
            supplierOrderMap[supplierId] = {
              sellerOpenId: supplierId,
              companyName: productInfo.companyName || "Unknown Supplier",
              orders: [],
              items: [],
              totalSkus: 0,
              totalQuantity: 0,
            };
          }

          // Add order reference if not already added
          if (!supplierOrderMap[supplierId].orders.find(o => o.orderId === order.orderId)) {
            supplierOrderMap[supplierId].orders.push({
              _id: order._id,
              orderId: order.orderId,
              upsaleoOrderId: order.upsaleoOrderId,
              recipientName: order.recipientName,
              shippingAddress1: order.shippingAddress1,
              shippingCity: order.shippingCity,
              shippingState: order.shippingState,
              shippingPostalCode: order.shippingPostalCode,
              shippingPhoneNumber: order.shippingPhoneNumber,
            });
          }

          // AP_RMB from order breakdown (same for all variants of this order line) for cross-border summary
          const productIdForBreakdown = productInfo._id?.toString() || productInfo.productId?.toString() || "";
          let apRmbTotal = undefined;
          if (Array.isArray(order.breakdown) && productIdForBreakdown) {
            const breakdownEntry = order.breakdown.find(
              (b) => b.productId && (b.productId.toString() === productIdForBreakdown)
            );
            const apRmb = breakdownEntry?.breakdown?.[0]?.AP_RMB;
            if (apRmb != null && Number(apRmb) > 0) apRmbTotal = Number(apRmb);
          }

          // Process variants
          if (orderVariants.length > 0) {
            orderVariants.forEach(variant => {
              // specId: prefer order-level variant if skuId matches, then variants[] entry, then product
              const orderVariant = item.variant || {};
              const useOrderVariantSpecId = orderVariant.specId && (
                !orderVariant.skuId || String(orderVariant.skuId) === String(variant.skuId)
              );
              const specId = useOrderVariantSpecId ? String(orderVariant.specId) : (variant.specId || "");

              const variantAttrs = variant.skuAttributes || orderVariant.skuAttributes || [];
              const variantName = variantAttrs.map(attr => attr.valueTrans || attr.value || "").filter(v => v).join(" / ") || "";
              const vendorPrice = variant.ap || orderVariant.ap || 0;
              const variantQty = variant.quantity || 1;
              const variantSkuId = variant.skuId?.toString() || item.sku;

              const itemData = {
                orderId: order.orderId,
                upsaleoOrderId: order.upsaleoOrderId,
                sku: variantSkuId,
                parentSku: item.sku,
                offerId: productInfo.offerId || "",
                specId,
                quantity: variantQty,
                price: item.consumerPrice || 0,
                vendorPrice,
                productTitle: productInfo.title || "",
                variantName,
                image: variantAttrs[0]?.skuImageUrl || item.image || productInfo.imgUrl?.[0] || "",
                productId: productInfo._id?.toString() || productIdForBreakdown || undefined,
                apRmbTotal,
                skuAttributes: variantAttrs,
              };

              // Include globalOrderQuantity if available
              if (item.globalOrderQuantity !== undefined && item.globalOrderQuantity !== null && item.globalOrderQuantity > 0) {
                itemData.globalOrderQuantity = item.globalOrderQuantity;
              }

              supplierOrderMap[supplierId].items.push(itemData);
              supplierOrderMap[supplierId].totalSkus += 1;
              supplierOrderMap[supplierId].totalQuantity += variantQty;
            });
          } else {
            // Use order-level variant data (item.variant singular) first, fall back to product variations
            const orderVariant = item.variant || {};
            const skuIdNum = parseInt(item.sku);
            const matchedVariation = productInfo.variations?.find(v =>
              String(v.skuId) === String(item.sku) || v.skuId === skuIdNum
            ) || productInfo.variations?.[0];

            const specId = (orderVariant.specId ? String(orderVariant.specId) : "") || matchedVariation?.specId || "";
            const vendorPrice = orderVariant.ap || matchedVariation?.ap || productInfo.vendor_price || 0;
            const variantAttrs = orderVariant.skuAttributes || matchedVariation?.skuAttributes || [];
            const variantName = variantAttrs.map(attr => attr.valueTrans || attr.value || "").filter(v => v).join(" / ") || "";

            const itemData = {
              orderId: order.orderId,
              upsaleoOrderId: order.upsaleoOrderId,
              sku: item.sku,
              offerId: orderVariant.offerId || productInfo.offerId || "",
              specId,
              quantity: item.quantity || 1,
              price: item.consumerPrice || 0,
              vendorPrice,
              productTitle: productInfo.title || "",
              variantName,
              image: variantAttrs[0]?.skuImageUrl || item.image || productInfo.imgUrl?.[0] || "",
              productId: productInfo._id?.toString() || productIdForBreakdown || undefined,
              apRmbTotal,
              skuAttributes: variantAttrs,
            };

            // Include globalOrderQuantity if available
            if (item.globalOrderQuantity !== undefined && item.globalOrderQuantity !== null && item.globalOrderQuantity > 0) {
              itemData.globalOrderQuantity = item.globalOrderQuantity;
            }

            supplierOrderMap[supplierId].items.push(itemData);
            supplierOrderMap[supplierId].totalSkus += 1;
            supplierOrderMap[supplierId].totalQuantity += item.quantity || 1;
          }
        });
      });

      // Convert to array and add split info — filter out unknown suppliers
      const supplierGroups = Object.values(supplierOrderMap)
        .filter(group => group.sellerOpenId && group.sellerOpenId !== "unknown")
        .map(group => ({
          ...group,
          orderCount: group.orders.length,
          needsSplit: group.totalSkus > 50,
          splitCount: Math.ceil(group.totalSkus / 50),
        }));

      const skippedUnknown = Object.values(supplierOrderMap).filter(g => !g.sellerOpenId || g.sellerOpenId === "unknown");
      let skippedOrdersInfo = [];
      if (skippedUnknown.length > 0) {
        const skippedOrderCount = skippedUnknown.reduce((sum, g) => sum + g.orders.length, 0);
        message.showToast(`${skippedOrderCount} order(s) skipped — supplier not found`, ToastTypes.WARNING);
        // Collect detailed info about skipped orders
        const skippedIds = new Set();
        skippedUnknown.forEach(g => {
          g.orders.forEach(o => {
            const skus = g.items.filter(i => (i.orderId === o.orderId || i.upsaleoOrderId === o.upsaleoOrderId)).map(i => i.sku).filter(Boolean);
            skippedOrdersInfo.push({
              orderId: o.orderId,
              upsaleoOrderId: o.upsaleoOrderId,
              skus,
              reason: "Supplier not found — product may not have a valid sellerOpenId mapped",
            });
            if (o.orderId) skippedIds.add(o.orderId);
            if (o.upsaleoOrderId) skippedIds.add(o.upsaleoOrderId);
          });
        });
      } else {
      }

      loader.hideLoader();

      if (supplierGroups.length === 0) {
        setIsProcessingCrossBorder(false);
        message.showToast("No valid supplier data found for selected orders", ToastTypes.ERROR);
        return;
      }

      // Navigate to cross-border summary with grouped data — navigate before resetting state
      // to avoid re-render interference
      navigate("/admin/cross-border-orders/summary", {
        state: { selectedSuppliers: supplierGroups, skippedOrders: skippedOrdersInfo }
      });
      setIsProcessingCrossBorder(false);

    } catch (error) {
      loader.hideLoader();
      setIsProcessingCrossBorder(false);
      message.showToast(error?.message || "Failed to prepare cross-border orders", ToastTypes.ERROR);
    }
  };

  const handleDeleteClick = (order) => {
    setDeleteModal({
      open: true,
      order: order,
      password: "",
      showPassword: false,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.order?._id || !deleteModal.password) {
      message.showToast("Please enter password", ToastTypes.WARNING);
      return;
    }

    loader.showInlineLoader("Deleting order...", "medium");
    try {
      const res = await orderApiService.deleteOrder(deleteModal.order._id, deleteModal.password);
      loader.hideLoader();

      if (res.status.code === 200) {
        message.showToast("Order deleted successfully", ToastTypes.SUCCESS);
        setDeleteModal({ open: false, order: null, password: "", showPassword: false });
        if (onRefresh) onRefresh();
      } else {
        message.showToast(res.error?.message || "Failed to delete order", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Error deleting order", ToastTypes.ERROR);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedOrders.length === 0) {
      message.showToast("Please select at least one order to delete", ToastTypes.WARNING);
      return;
    }
    setBulkDeleteModal({ open: true, password: "", showPassword: false });
  };

  const handleBulkDeleteConfirm = async () => {
    if (!bulkDeleteModal.password) {
      message.showToast("Please enter password", ToastTypes.WARNING);
      return;
    }

    if (selectedOrders.length === 0) {
      message.showToast("No orders selected", ToastTypes.WARNING);
      return;
    }

    const orderIds = selectedOrders.map(order => order._id).filter(Boolean);

    if (orderIds.length === 0) {
      message.showToast("No valid orders to delete", ToastTypes.WARNING);
      return;
    }

    loader.showInlineLoader(`Deleting ${orderIds.length} order(s)...`, "medium");

    try {
      const res = await orderApiService.deleteOrder(null, bulkDeleteModal.password, orderIds);
      loader.hideLoader();

      if (res.status.code === 200) {
        const deletedCount = res.data?.deletedCount || 0;
        const requestedCount = res.data?.requestedCount || orderIds.length;

        if (deletedCount === requestedCount) {
          message.showToast(`Successfully deleted ${deletedCount} order(s)`, ToastTypes.SUCCESS);
        } else {
          message.showToast(`Deleted ${deletedCount} of ${requestedCount} order(s)`, ToastTypes.WARNING);
        }

        setBulkDeleteModal({ open: false, password: "", showPassword: false });
        setSelectedOrders([]);

        if (onRefresh) onRefresh();
      } else {
        message.showToast(res.error?.message || "Failed to delete orders", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || "Error deleting orders", ToastTypes.ERROR);
    }
  };

  // Confirm/Reject Order Handlers
  const handleConfirmOrder = (order) => {
    setConfirmRejectModal({ open: true, order, action: 'confirm' });
  };

  const isLikelyBankTransferOrder = (order) => {
    if (!order) return false;
    if (order?.bankTransferTxnId || order?.paymentProofUrl) return true;

    // const isPaymentPending = (order?.status || '').toLowerCase() === 'payment pending';
    // if (!isPaymentPending) return false;

    const history = Array.isArray(order?.orderStatus) ? order.orderStatus : [];
    return history.some((item) => {
      const statusText = (item?.status || '').toLowerCase();
      return statusText.includes('awaiting payment confirmation') || statusText.includes('awaiting confirmation');
    });
  };

  const getPaymentSource = (order) => {
    if (order?.razorpayPaymentId || order?.razorpayOrderId) return "Razorpay";
    if (order?.ccavenueOrderId || order?.ccavenueTrackingId) return "CCAvenue";
    if (isLikelyBankTransferOrder(order)) return "Bank Transfer";
    if (order?.transactionId) return "IM Cash";
    return "—";
  };

  const handleBtConfirmSubmit = () => {
    const { order, adminNote } = btConfirmModal;
    if (!order?._id) return;
    setBtConfirmModal((prev) => ({ ...prev, submitting: true }));
    bankTransferApiService
      .confirmBankTransfer(order._id, adminNote)
      .then((res) => {
        if (res?.status?.code === 200) {
          message.showToast("Bank transfer payment confirmed! Order is now processing.", ToastTypes.SUCCESS);
          setBtConfirmModal({ open: false, order: null, adminNote: "", submitting: false });
          if (onRefresh) onRefresh();
        } else {
          message.showToast(res?.error?.message || "Failed to confirm.", ToastTypes.ERROR);
          setBtConfirmModal((prev) => ({ ...prev, submitting: false }));
        }
      })
      .catch(() => {
        message.showToast("Error confirming payment.", ToastTypes.ERROR);
        setBtConfirmModal((prev) => ({ ...prev, submitting: false }));
      });
  };

  const handleRejectOrder = (order) => {
    if (!order?._id) {
      message.showToast("Invalid order data", ToastTypes.ERROR);
      return;
    }
    // Step 1: open confirmation modal first
    setConfirmRejectModal({ open: true, order, action: 'reject' });
  };

  const buildRejectRefundOrderData = (order) => {
    if (!order?._id) return null;
    const orderAmount = Number(order?.importerPrice) || 0;
    const shippingCharges = Number(order?.totalShippingCharges) || 0;
    return {
      orderId: order._id,
      orderAmount,
      shippingCharges,
      totalAmount: orderAmount + shippingCharges,
      sellerId: order?.sellerId,
      upsaleoOrderId: order?.upsaleoOrderId || order?.orderId,
      ticketId: null,
      status: order?.status,
      paymentSource: getPaymentSource(order),
      variant: "rejectOrder",
    };
  };

  const handleRejectConfirmThenOpenRefund = () => {
    if (!confirmRejectModal.order?._id) return;
    const orderData = buildRejectRefundOrderData(confirmRejectModal.order);
    if (orderData) {
      setRejectRefundOrder(orderData);
    }
    setConfirmRejectModal({ open: false, order: null, action: null });
  };

  const handleConfirmRejectAction = async () => {
    if (!confirmRejectModal.order?._id || !confirmRejectModal.action) {
      message.showToast("Invalid order or action", ToastTypes.ERROR);
      return;
    }

    setIsProcessingAction(true);
    loader.showLoading(true);

    try {
      const orderId = confirmRejectModal.order._id;
      const action = confirmRejectModal.action;

      // Map action to API value
      const apiValue = action === 'confirm' ? 'confirm' : 'cancel';

      // Call the confirmation API endpoint
      const res = await orderApiService.updateOrderConfirmation(orderId, apiValue);

      if (res.status.code === 200) {
        const actionText = action === 'confirm' ? 'confirmed' : 'rejected';
        message.showToast(`Order ${confirmRejectModal.order.upsaleoOrderId || orderId} ${actionText} successfully`, ToastTypes.SUCCESS);

        setConfirmRejectModal({ open: false, order: null, action: null });

        if (onRefresh) onRefresh();
      } else {
        message.showToast(res.error?.message || `Failed to ${action} order`, ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || `Error ${confirmRejectModal.action}ing order`, ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
      setIsProcessingAction(false);
    }
  };

  const handleCancelConfirmReject = () => {
    setConfirmRejectModal({ open: false, order: null, action: null });
  };

  const handleRejectRefundSuccess = async () => {
    if (!rejectRefundOrder?.orderId) return;
    setIsProcessingAction(true);
    loader.showLoading(true);
    try {
      const res = await orderApiService.updateOrderConfirmation(rejectRefundOrder.orderId, 'cancel');
      if (res.status.code === 200) {
        message.showToast(`Order ${rejectRefundOrder.upsaleoOrderId || rejectRefundOrder.orderId} rejected and refund processed`, ToastTypes.SUCCESS);
        setRejectRefundOrder(null);
        if (onRefresh) onRefresh();
      } else {
        message.showToast(res.error?.message || "Order rejected but failed to update status", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Error updating order status", ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
      setIsProcessingAction(false);
    }
  };

  function getStatusBadge(status) {
    const statusConfig = {
      'payment pending': { class: 'status-pending', icon: 'fas fa-clock' },
      'processing': { class: 'status-processing', icon: 'fas fa-cog' },
      'shipped': { class: 'status-shipped', icon: 'fas fa-truck' },
      'delivered': { class: 'status-delivered', icon: 'fas fa-check-circle' },
      'cancel': { class: 'status-cancelled', icon: 'fas fa-times-circle' },
      'abnormal': { class: 'status-abnormal', icon: 'fas fa-exclamation-triangle' }
    };

    if (!status) {
      return (
        <span className="status-badge status-default">
          <i className="fas fa-circle"></i>
          N/A
        </span>
      );
    }

    const key = String(status).toLowerCase();
    const config = statusConfig[key] || { class: 'status-default', icon: 'fas fa-circle' };

    return (
      <span className={`status-badge ${config.class}`}>
        <i className={config.icon}></i>
        {status}
      </span>
    );
  }

  function getCrossBorderStatusBadge(status) {
    const statusConfig = {
      // Internal status values
      'pending': { class: 'status-pending', icon: 'fas fa-clock', color: '#f59e0b', label: 'Pending' },
      'processing': { class: 'status-processing', icon: 'fas fa-cog', color: '#3b82f6', label: 'Processing' },
      'payment_pending': { class: 'status-pending', icon: 'fas fa-credit-card', color: '#f59e0b', label: 'Payment Pending' },
      'paid': { class: 'status-processing', icon: 'fas fa-check-circle', color: '#10b981', label: 'Paid' },
      'shipped': { class: 'status-shipped', icon: 'fas fa-truck', color: '#6366f1', label: 'Shipped' },
      'delivered': { class: 'status-delivered', icon: 'fas fa-check-circle', color: '#10b981', label: 'Delivered' },
      'cancelled': { class: 'status-cancelled', icon: 'fas fa-times-circle', color: '#ef4444', label: 'Cancelled' },
      'failed': { class: 'status-abnormal', icon: 'fas fa-exclamation-triangle', color: '#ef4444', label: 'Failed' },
      'ticketed': { class: 'status-abnormal', icon: 'fas fa-ticket-alt', color: '#f59e0b', label: 'Ticketed' },
      // Alibaba/1688 status values - formatted like cross-border order page
      'wait buyer pay': { class: 'status-pending', icon: 'fas fa-credit-card', color: '#f59e0b', label: 'Wait Buyer Pay' },
      'waitbuyerpay': { class: 'status-pending', icon: 'fas fa-credit-card', color: '#f59e0b', label: 'Wait Buyer Pay' },
      'wait_buyer_pay': { class: 'status-pending', icon: 'fas fa-credit-card', color: '#f59e0b', label: 'Wait Buyer Pay' },
      'buyer pay timeout': { class: 'status-abnormal', icon: 'fas fa-exclamation-triangle', color: '#ef4444', label: 'Buyer Pay Timeout' },
      'buyerpaytimeout': { class: 'status-abnormal', icon: 'fas fa-exclamation-triangle', color: '#ef4444', label: 'Buyer Pay Timeout' },
      'buyer_pay_timeout': { class: 'status-abnormal', icon: 'fas fa-exclamation-triangle', color: '#ef4444', label: 'Buyer Pay Timeout' },
      'wait seller send goods': { class: 'status-processing', icon: 'fas fa-box', color: '#3b82f6', label: 'Wait Seller Send' },
      'waitsellersendgoods': { class: 'status-processing', icon: 'fas fa-box', color: '#3b82f6', label: 'Wait Seller Send' },
      'waitsellersend': { class: 'status-processing', icon: 'fas fa-box', color: '#3b82f6', label: 'Wait Seller Send' },
      'wait_seller_send_goods': { class: 'status-processing', icon: 'fas fa-box', color: '#3b82f6', label: 'Wait Seller Send' },
      'wait buyer accept goods': { class: 'status-shipped', icon: 'fas fa-truck', color: '#6366f1', label: 'Wait Buyer Receive' },
      'waitbuyeracceptgoods': { class: 'status-shipped', icon: 'fas fa-truck', color: '#6366f1', label: 'Wait Buyer Receive' },
      'waitbuyerreceive': { class: 'status-shipped', icon: 'fas fa-truck', color: '#6366f1', label: 'Wait Buyer Receive' },
      'wait_buyer_accept_goods': { class: 'status-shipped', icon: 'fas fa-truck', color: '#6366f1', label: 'Wait Buyer Receive' },
      'success': { class: 'status-delivered', icon: 'fas fa-check-circle', color: '#10b981', label: 'Success' },
      'closed': { class: 'status-cancelled', icon: 'fas fa-times-circle', color: '#ef4444', label: 'Closed' },
      'cancel': { class: 'status-cancelled', icon: 'fas fa-times-circle', color: '#ef4444', label: 'Cancelled' },
      'terminated': { class: 'status-failed', icon: 'fas fa-ban', color: '#ef4444', label: 'Terminated' },
      'waitselleract': { class: 'status-processing', icon: 'fas fa-hourglass-half', color: '#3b82f6', label: 'Wait Seller Act' },
      'waitbuyeract': { class: 'status-pending', icon: 'fas fa-user-clock', color: '#f59e0b', label: 'Wait Buyer Act' },
      'confirm_goods': { class: 'status-delivered', icon: 'fas fa-check-circle', color: '#10b981', label: 'Confirmed' }
    };

    if (!status) {
      return null;
    }

    // Normalize status key (lowercase, remove spaces/underscores for matching)
    const normalizedStatus = String(status).toLowerCase().replace(/\s+/g, '').replace(/_/g, '');
    const config = statusConfig[normalizedStatus] || statusConfig[String(status).toLowerCase()] || { class: 'status-default', icon: 'fas fa-circle', color: '#6b7280', label: status || 'N/A' };

    // Display formatted status (use label if available, otherwise use original status)
    const displayStatus = config.label || status;

    return (
      <span
        className={`status-badge ${config.class}`}
        style={{ backgroundColor: `${config.color}15`, color: config.color, borderColor: `${config.color}40` }}
        title={`Cross-Border Order Status: ${displayStatus}`}
      >
        <i className={config.icon}></i>
        {displayStatus}
      </span>
    );
  }

  const columns = useMemo(() => {
    const hideImporterrMkpCols = sourceTab === 'importerr';
    const hideAmazonMkpAmountCols = sourceTab === 'amazon';
    const mkpOrderIdHoverLabel =
      sourceTab === "importerr"
        ? "Importerr Order ID"
        : sourceTab === "amazon"
          ? "Amazon Order ID"
          : sourceTab === "shopify"
            ? "Shopify Order ID"
            : "MKP Order ID";
    const baseColumns = [
      {
        key: "serialNumber",
        label: "S.No.",
        width: 80,
        render: (value, row) => {
          const key = row?.upsaleoOrderId || row?.orderId;
          const warnings = key ? orderWarnings[key] : null;
          return (
            <span className="serial-number-cell" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {value ?? "-"}
              {warnings && (
                <span className="cb-warning-hint" title={'⚠ Cross-border issues:\n' + warnings.join('\n')}>
                  <MdWarning style={{ color: '#f59e0b', fontSize: '14px', cursor: 'help' }} />
                </span>
              )}
            </span>
          );
        },
      },
      // India S.No column - only show for mapped orders tab
      ...(activeTab === 'mapped' ? [{
        key: "indiaSNo",
        label: "India S.No",
        width: 100,
        render: (_, row) => {
          const indiaSNo = row?.originalExcelData?.['India S.No'] || row?.originalExcelData?.['India S.No.'] || '-';
          return (
            <span style={{
              fontWeight: 600,
              color: indiaSNo !== '-' ? '#7c3aed' : '#9ca3af',
              backgroundColor: indiaSNo !== '-' ? '#f3e8ff' : 'transparent',
              padding: indiaSNo !== '-' ? '2px 6px' : '0',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              {indiaSNo}
            </span>
          );
        },
      }] : []),
      {
        key: "tickets",
        label: "Actions",
        width: 300,
        render: (_, row) => {
          const hasTicket = row?.ticket && row?.ticketId;
          const canShowActions = row.status === 'payment pending' || row.status === 'processing';
          const hasConfirmationStatus = row?.orderConfirmationStatus;

          return (
            <div className="om-tickets-cell" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Ticket Button */}
              {hasTicket ? (
                <button
                  type="button"
                  className="om-ticket-btn om-ticket-view"
                  onClick={() => {
                    setTicketPanelTicketId(row.ticketId);
                    setTicketPanelOrderLabel(row.upsaleoOrderId || null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    fontWeight: '500',
                    border: '1px solid #3b82f6',
                    background: '#fff',
                    color: '#3b82f6',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#3b82f6';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.color = '#3b82f6';
                  }}
                  title="View ticket"
                >
                  <i className="fas fa-ticket-alt" style={{ marginRight: '4px' }} />
                  View ticket
                </button>
              ) : (
                <button
                  type="button"
                  className="om-ticket-btn om-ticket-raise"
                  onClick={() => setTicketModalOrderId(row._id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    fontWeight: '500',
                    border: '1px solid #8b5cf6',
                    background: '#fff',
                    color: '#8b5cf6',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#8b5cf6';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.color = '#8b5cf6';
                  }}
                  title="Raise ticket"
                >
                  <MdAdd style={{ fontSize: '12px' }} />
                  Raise ticket
                </button>
              )}

              {/* Split button - only when order has 2+ different offer IDs */}
              {canEdit("order-management") && (row.uniqueOfferIdCount >= 2) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSplitClick(row);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    fontWeight: '500',
                    border: '1px solid #f59e0b',
                    background: '#fff',
                    color: '#f59e0b',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f59e0b';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.color = '#f59e0b';
                  }}
                  title="Split order by offer ID – create a new order with selected items"
                >
                  <i className="fas fa-code-branch" style={{ fontSize: '11px' }} />
                  Split
                </button>
              )}

              {/* Bank Transfer Confirm Button - only for payment pending bank transfer orders */}
              {row.status === 'payment pending' && isLikelyBankTransferOrder(row) && canEdit("order-management") && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBtConfirmModal({ open: true, order: row, adminNote: "", submitting: false });
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    border: '1px solid #3dd0e5',
                    background: '#3dd0e5',
                    color: '#fff',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#26b9d2'; e.currentTarget.style.borderColor = '#26b9d2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#3dd0e5'; e.currentTarget.style.borderColor = '#3dd0e5'; }}
                  title="Confirm bank transfer payment and move order to processing"
                >
                  <MdCheckCircle style={{ fontSize: '13px' }} />
                  Confirm Transfer
                </button>
              )}

              {/* Confirm/Reject Buttons - only show for eligible statuses and no confirmation status */}
              {canShowActions && canEdit("order-management") && !hasConfirmationStatus && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfirmOrder(row);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: '500',
                      border: '1px solid #10b981',
                      background: '#fff',
                      color: '#10b981',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#10b981';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#fff';
                      e.target.style.color = '#10b981';
                    }}
                    title="Confirm order"
                  >
                    <MdCheckCircle style={{ fontSize: '12px' }} />
                    Confirm
                  </button>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRejectOrder(row);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontWeight: '500',
                      border: '1px solid #ef4444',
                      background: '#fff',
                      color: '#ef4444',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#ef4444';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#fff';
                      e.target.style.color = '#ef4444';
                    }}
                    title="Reject order"
                  >
                    <MdClose style={{ fontSize: '12px' }} />
                    Reject
                  </button>
                </>
              )}

              {/* Delete Button */}
              {canDelete("order-management") && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(row);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    fontWeight: '500',
                    border: '1px solid #dc2626',
                    background: '#fff',
                    color: '#dc2626',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#dc2626';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.color = '#dc2626';
                  }}
                  title="Delete order (requires password)"
                >
                  <i className="fas fa-trash" style={{ fontSize: "11px" }}></i>
                  Delete
                </button>
              )}
            </div>
          );
        },
      },
      {
        key: "upsaleoOrderId",
        label: "Importerr ID",
        width: 140,
        render: (value, row) => {
          // Check for customer note - check multiple possible field names
          const customerNote = row?.customerNote || row?.note || row?.customer_note;
          const hasNote = customerNote && String(customerNote).trim().length > 0;

          const handleMouseEnter = (e) => {
            if (!hasNote) return;
            const tooltip = e.currentTarget.querySelector('.customer-note-tooltip-card');
            if (tooltip) {
              const rect = e.currentTarget.getBoundingClientRect();
              tooltip.style.top = `${rect.bottom + 5}px`;
              tooltip.style.left = `${rect.left}px`;
            }
          };

          // Calculate SKUs count and total quantity
          let totalSkus = 0;
          let totalQuantity = 0;

          if (Array.isArray(row?.orderDetails)) {
            // Count unique SKUs (variants)
            const uniqueSkus = new Set();
            row.orderDetails.forEach(detail => {
              const sku = detail?.variant?.skuId || detail?.sku || detail?.variant?.sku || '';
              if (sku) uniqueSkus.add(sku);

              // Sum quantities
              const quantity = Number(detail?.quantity) || 0;
              totalQuantity += quantity;
            });
            totalSkus = uniqueSkus.size;
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span
                  className="cell-link"
                  title={`Importerr Order ID: ${value || "-"}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRowNavigate(row);
                  }}
                >
                  {value || "-"}
                </span>
                {hasNote && (
                  <div className="customer-note-tooltip-wrapper" onMouseEnter={handleMouseEnter}>
                    <span
                      className="customer-note-badge"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 6px',
                        fontSize: '10px',
                        fontWeight: '500',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        borderRadius: '4px',
                        border: '1px solid #93c5fd',
                        cursor: 'pointer',
                      }}
                    >
                      <i className="fas fa-sticky-note" style={{ fontSize: '8px' }}></i>
                      Note
                    </span>
                    <div className="customer-note-tooltip-card">
                      <div className="customer-note-tooltip-header">
                        <i className="fas fa-sticky-note" style={{ marginRight: '6px' }}></i>
                        <strong>Customer Note</strong>
                      </div>
                      <div className="customer-note-tooltip-content">
                        {String(customerNote)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Show SKUs and quantity info */}
              {(totalSkus > 0 || totalQuantity > 0) && (
                <div style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  lineHeight: '1.2',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1px'
                }}>
                  {totalSkus > 0 && (
                    <span>SKUs: {totalSkus}</span>
                  )}
                  {totalQuantity > 0 && (
                    <span>Quantity: {totalQuantity}</span>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      ...(hideImporterrMkpCols || sourceTab === "shopify" ? [] : [
        {
          key: "orderId",
          label: "Sub Order ID",
          headerTitle: mkpOrderIdHoverLabel,
          width: 130,
          render: (value, row) => (
            <span
              className="cell-link"
              title={`${mkpOrderIdHoverLabel}: ${value || "-"}`}
              onClick={(e) => {
                e.stopPropagation();
                handleRowNavigate(row);
              }}
            >
              {value || "-"}
            </span>
          ),
        },
      ]),
      ...(hideImporterrMkpCols || sourceTab === "amazon" ? [] : [
        {
          key: "orderItemId",
          label: sourceTab === "shopify" ? "Sub Order ID" : "Order Item ID",
          headerTitle: sourceTab === "shopify" ? "Shopify Order ID" : "Order Item ID",
          width: 130,
          render: (_, row) => {
            // Get all orderItemIds from orderDetails
            const orderItemIds = row.orderDetails?.map(detail => detail?.orderItemId).filter(Boolean) || [];
            // Remove duplicates
            const uniqueIds = [...new Set(orderItemIds)];
            // Show single value or comma-separated if multiple unique IDs
            const displayValue = uniqueIds.length > 0 ? uniqueIds.join(', ') : '-';
            return (
              <span
                className="cell-link"
                title={sourceTab === "shopify" ? `Shopify Order ID: ${displayValue}` : `Order Item ID: ${displayValue}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowNavigate(row);
                }}
              >
                {displayValue}
              </span>
            );
          },
        },
      ]),
      {
        key: "sellerId",
        label: "Account Details",
        width: 180,
        render: (_, row) => {
          const seller = row?.sellerId;

          if (!seller) return "-";

          const sellerName = `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
          const uniqueId = seller?.uniqueId || "-";

          return (
            <div className="customer-cell">
              <span
                className="customer-name seller-link"
              // style={{ cursor: "pointer", color: "#1677ff" }}
              // onClick={(e) => {
              //   e.stopPropagation(); // ✅ prevent row click
              //   navigate(`/admin/user-management/edit-user/${seller._id}`);
              // }}
              >
                {sellerName} <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '500' }}>({uniqueId})</span>
              </span>

              {seller?.email && (
                <span className="customer-sub">{seller.email}</span>
              )}
            </div>
          );
        },
      },
      {
        key: "recipientName",
        label: "Customer Name",
        width: 120,
        render: (value, row) => {
          const handleMouseEnter = (e) => {
            const tooltip = e.currentTarget.querySelector('.address-tooltip-card');
            if (tooltip) {
              const rect = e.currentTarget.getBoundingClientRect();
              tooltip.style.top = `${rect.bottom + 5}px`;
              tooltip.style.left = `${rect.left}px`;
            }
          };

          // Mapped orders: buyer + address + city/state/pin/mobile from Excel (or row), same hover pattern as Orders tab
          if (activeTab === 'mapped') {
            const ex = row?.originalExcelData || {};
            const mobile =
              ex["Mob. No."] || ex["Mobile"] || row?.shippingPhoneNumber || "";
            const city = ex["City"] || row?.shippingCity || "";
            const state = ex["State"] || row?.shippingState || "";
            const pin = ex["Pincode"] || ex["Pinode"] || row?.shippingPostalCode || "";
            const addrLine = ex["Buyer Address"] || "";
            const fromExcel = [addrLine, [city, state, pin].filter(Boolean).join(", ")].filter(Boolean).join(", ");
            const fromRowShip = [
              row?.shippingAddress1,
              row?.shippingAddress2,
              row?.shippingAddress3,
              row?.shippingCity,
              row?.shippingState,
              row?.shippingPostalCode,
            ]
              .filter(Boolean)
              .join(", ");
            const shippingAddress = fromExcel || fromRowShip;

            const billingAddress = [
              row?.billingAddress1,
              row?.billingAddress2,
              row?.billingAddress3,
              row?.billingCity,
              row?.billingState,
              row?.billingPostalCode,
            ]
              .filter(Boolean)
              .join(", ");

            const displayName =
              (value && String(value).trim()) ||
              ex["Buyer Name"] ||
              "-";

            const hasTooltip = Boolean(mobile || shippingAddress || billingAddress);

            return (
              <div className="recipient-tooltip-wrapper" onMouseEnter={handleMouseEnter}>
                <span className="recipient-name">{displayName}</span>
                {hasTooltip ? (
                  <div className="address-tooltip-card">
                    {mobile ? (
                      <div className="address-section">
                        <strong>Mobile:</strong>
                        <span>{mobile}</span>
                      </div>
                    ) : null}
                    {shippingAddress ? (
                      <div className="address-section">
                        <strong>Shipping Address:</strong>
                        <span>{shippingAddress}</span>
                      </div>
                    ) : null}
                    {billingAddress ? (
                      <div className="address-section">
                        <strong>Billing Address:</strong>
                        <span>{billingAddress}</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          }

          // Check for self pickup (only for importerr tab)
          const isSelfPickup = sourceTab === 'importerr' &&
            (row?.shippingMethod?.toLowerCase().includes('self-pickup'));

          const shippingAddress = [
            row?.shippingAddress1,
            row?.shippingAddress2,
            row?.shippingAddress3,
            row?.shippingCity,
            row?.shippingState,
            row?.shippingPostalCode
          ].filter(Boolean).join(", ");

          const billingAddress = [
            row?.billingAddress1,
            row?.billingAddress2,
            row?.billingAddress3,
            row?.billingCity,
            row?.billingState,
            row?.billingPostalCode
          ].filter(Boolean).join(", ");

          const hasAddress = shippingAddress || billingAddress;

          return (
            <div className="recipient-tooltip-wrapper" onMouseEnter={handleMouseEnter}>
              <span
                className="recipient-name"
                style={{
                  color: isSelfPickup ? '#dc2626' : 'inherit',
                  fontWeight: isSelfPickup ? '600' : 'inherit'
                }}
              >
                {isSelfPickup ? 'Self Pickup' : (value || "-")}
              </span>
              {hasAddress && (
                <div className="address-tooltip-card">
                  {shippingAddress && (
                    <div className="address-section">
                      <strong>Shipping Address:</strong>
                      <span>{shippingAddress}</span>
                    </div>
                  )}
                  {billingAddress && (
                    <div className="address-section">
                      <strong>Billing Address:</strong>
                      <span>{billingAddress}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        },
      },

      ...(hideImporterrMkpCols ? [] : [
        {
          key: "marketplacePlatform",
          label: "Source",
          width: 180,
          filterAccessor: (_, row) => {
            const synthetic = {
              marketplace: row?.marketplace,
              panel: row?.panel,
              orderDetails: row?.orderDetails,
              originalExcelData: row?.originalExcelData,
            };
            const meta = getOrderPlatformMeta(synthetic);
            const mkp = row?.marketplace && String(row.marketplace).trim() ? String(row.marketplace).trim() : "";
            return [meta?.label, mkp].filter(Boolean).join(" ");
          },
          render: (_, row) => {
            const synthetic = {
              marketplace: row?.marketplace,
              panel: row?.panel,
              orderDetails: row?.orderDetails,
              originalExcelData: row?.originalExcelData,
            };
            const meta = getOrderPlatformMeta(synthetic);
            const rawMkp = row?.marketplace && String(row.marketplace).trim() ? String(row.marketplace).trim() : "";
            const marketplaceName = rawMkp
              ? (rawMkp.toLowerCase() === "manual" ? "MANUAL" : rawMkp)
              : (meta?.key !== "unknown" ? meta?.label : "");
            const isImporterrMkp =
              String(meta?.key || "").toLowerCase() === "importerr" ||
              String(marketplaceName || "").toLowerCase().includes("importerr");

            if (!marketplaceName && (!meta || meta.key === "unknown")) return "-";

            if (marketplaceName === "MANUAL") {
              return <span style={{ fontWeight: 600 }}>MANUAL</span>;
            }

            if (sourceTab === "all" && isImporterrMkp) {
              return <OrderPlatformBadge order={synthetic} size={18} compactImporterrWordmark />;
            }

            return (
              <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <OrderPlatformBadge order={synthetic} size={18} compactImporterrWordmark />
                <span style={{ fontWeight: 500, color: "#374151" }}>{marketplaceName || "-"}</span>
              </div>
            );
          },
        },
      ]),
      {
        key: "status",
        label: "Order Status",
        width: 150,
        render: (value, row) => {
          // If cross-border status exists, show it instead of regular status
          const crossBorderStatus = row?.crossBorderStatus;
          const statusToShow = crossBorderStatus || value;
          const statusBadge = crossBorderStatus
            ? getCrossBorderStatusBadge(crossBorderStatus)
            : getStatusBadge(value);

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {statusBadge}
              {row?.fromFailedOrder && (
                <span
                  className="from-failed-order-badge"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: '500',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    borderRadius: '4px',
                    border: '1px solid #fcd34d',
                  }}
                >
                  <i className="fas fa-exchange-alt" style={{ fontSize: '8px' }}></i>
                  From Failed Order
                </span>
              )}
              {/* Show orderConfirmationStatus if it exists */}
              {row?.orderConfirmationStatus && (
                <span
                  className="order-confirmation-status-badge"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: '500',
                    backgroundColor: '#f0f9ff',
                    color: '#1e40af',
                    borderRadius: '4px',
                    border: '1px solid #93c5fd',
                  }}
                >
                  <i className="fas fa-info-circle" style={{ fontSize: '8px' }}></i>
                  {row.orderConfirmationStatus === 'confirm' ? 'Confirmed' : row.orderConfirmationStatus === 'cancel' ? 'Rejected' : row.orderConfirmationStatus}
                </span>
              )}
            </div>
          );
        },
      },
      ...(sourceTab === "shopify" ? [{
        key: "importerr_payment_status",
        label: "PAYMENT STATUS",
        width: 150,
        // currenlty not showing anything
      }] : []),
      // Temporarily hide MKP payment status for Shopify; can enable again later.
      ...(sourceTab === 'amazon' || sourceTab === "shopify" ? [] : [{
        key: "mkp_financial_status",
        label: sourceTab === 'importerr' ? "PAYMENT STATUS" : "MKP PAYMENT STATUS",
        width: 150,
        render: (value, row) => {
          // Get mkp_financial_status from last item in mkpOrderStatusHistory array
          let displayValue = value;
          if (Array.isArray(row?.mkpOrderStatusHistory) && row.mkpOrderStatusHistory.length > 0) {
            const lastItem = row.mkpOrderStatusHistory[row.mkpOrderStatusHistory.length - 1];
            displayValue = lastItem?.mkp_order_status || value || '';
          }
          return getStatusBadge(displayValue);
        },
      }]),
      {
        key: "importerPrice",
        label: "Order Value",
        headerTitle: "Importerr Price",
        width: 160,
        render: (_, row) => {
          const finalAmount = Number(row.importerPrice) || 0;
          const imPointsDiscount = Number(row.imPointsDiscount) || 0;
          
          // New format: paymentBreakdown object present on the order
          const pb = row?.paymentBreakdown;
          const hasNewBreakdown = pb && typeof pb === 'object';

          // Derive payment method amounts
          const imPointsAmount = hasNewBreakdown ? (Number(pb.imPoints?.amount) || 0) : imPointsDiscount;
          const walletAmount = hasNewBreakdown ? (Number(pb.wallet?.amount) || 0) : 0;
          const razorpayAmount = hasNewBreakdown ? (Number(pb.razorpay?.amount) || 0) : 0;
          const ccavenueAmount = hasNewBreakdown ? (Number(pb.ccavenue?.amount) || 0) : 0;
          const bankTransferAmount = hasNewBreakdown ? (Number(pb.bankTransfer?.amount) || 0) : 0;

          // Flags for which methods were used
          const hasWallet = hasNewBreakdown ? walletAmount > 0 : (row?.transactionId && !(row?.razorpayPaymentId || row?.razorpayOrderId));
          const hasRazorpay = hasNewBreakdown ? razorpayAmount > 0 : !!(row?.razorpayPaymentId || row?.razorpayOrderId);
          const hasBankTransfer = hasNewBreakdown ? bankTransferAmount > 0 : !!(row?.bankTransferTxnId || row?.paymentProofUrl);
          const hasCCAvenue = hasNewBreakdown ? ccavenueAmount > 0 : !!(row?.ccavenueOrderId || row?.ccavenueTrackingId);
          const paymentSource = getPaymentSource(row);
          
          const hasImPoints = imPointsAmount > 0;

          // Calculate Grand Total
          // For new breakdown, it's the sum of all components
          // For legacy, it's finalAmount + imPointsDiscount
          const grandTotal = hasNewBreakdown 
            ? (imPointsAmount + walletAmount + razorpayAmount + ccavenueAmount + bankTransferAmount)
            : (finalAmount + imPointsDiscount);

          // Badge helper
          const Badge = ({ color, bg, children }) => (
            <span style={{
              fontSize: '9px',
              color,
              backgroundColor: bg,
              padding: '1px 5px',
              borderRadius: '4px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
              letterSpacing: '0.02em'
            }}>
              {children}
            </span>
          );

          return (
            <div className="price-breakdown-cell" style={{ 
              fontSize: '11px', 
              lineHeight: '1.6', 
              padding: '4px 0', 
              minWidth: '150px',
              color: '#374151'
            }}>
              {/* Grand Total Header */}
              <div style={{ 
                borderBottom: '1.5px solid #f3f4f6', 
                marginBottom: '6px', 
                paddingBottom: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: '600', color: '#6b7280', fontSize: '10px' }}>ORDER VALUE</span>
                <span style={{ fontWeight: '800', color: '#111827', fontSize: '12px' }}>{formatCurrency(grandTotal)}</span>
              </div>

              {/* Breakdown Rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {/* IM Points */}
                {hasImPoints && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Badge color="#059669" bg="#ecfdf5">IM Points</Badge>
                    <span style={{ fontWeight: '500', color: '#059669' }}>{formatCurrency(imPointsAmount)}</span>
                  </div>
                )}

                {/* Other Payment Methods */}
                {hasWallet && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Badge color="#7c3aed" bg="#f3e8ff">IM Cash</Badge>
                    <span style={{ fontWeight: '600', color: '#4b5563' }}>{formatCurrency(hasNewBreakdown ? walletAmount : finalAmount)}</span>
                  </div>
                )}
                {hasRazorpay && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Badge color="#2563eb" bg="#eff6ff">Razorpay</Badge>
                    <span style={{ fontWeight: '600', color: '#4b5563' }}>{formatCurrency(hasNewBreakdown ? razorpayAmount : finalAmount)}</span>
                  </div>
                )}
                {hasCCAvenue && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Badge color="#dc2626" bg="#fef2f2">CCAvenue</Badge>
                    <span style={{ fontWeight: '600', color: '#4b5563' }}>{formatCurrency(hasNewBreakdown ? ccavenueAmount : finalAmount)}</span>
                  </div>
                )}
                {hasBankTransfer && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Badge color="#b45309" bg="#fffbeb">Bank Transfer</Badge>
                    <span style={{ fontWeight: '600', color: '#4b5563' }}>{formatCurrency(hasNewBreakdown ? bankTransferAmount : finalAmount)}</span>
                  </div>
                )}

                {/* Fallback for legacy orders with unknown source */}
                {!hasWallet && !hasRazorpay && !hasCCAvenue && !hasBankTransfer && paymentSource && paymentSource !== "—" && (
                   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Badge color="#4b5563" bg="#f3f4f6">{paymentSource}</Badge>
                    <span style={{ fontWeight: '600', color: '#4b5563' }}>{formatCurrency(finalAmount)}</span>
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      // currently not used may enable later
      // {
      //   key: "imPointsDiscount",
      //   label: "IM Points (₹)",
      //   width: 120,
      //   render: (value) => (
      //     <span className="price-cell" style={{ color: value > 0 ? "#b45309" : "#6b7280" }}>
      //       {formatCurrency(value || 0)}
      //     </span>
      //   ),
      // },
      ...(hideImporterrMkpCols ? [] : [
        ...(hideAmazonMkpAmountCols ? [] : [
          // Temporarily hidden per UI request:
          // {
          //   key: "mkp_current_outstanding_price",
          //   label: "MKP Outstanding Amount",
          //   width: 120,
          //   render: (value) => value || "-",
          // },
          // {
          //   key: "mkp_current_received_price",
          //   label: "MKP Received Amount",
          //   width: 120,
          //   render: (value) => value || "-",
          // },
          {
            key: "mkpTotalWithShipping",
            label: "Seller Price",
            headerTitle: "Seller Selling Price",
            width: 150,
            render: (value) => (
              <span className="price-cell">{formatCurrency(value)}</span>
            ),
          },
        ]),
      ]),
      // currently not used may enable later
      // {
      //   key: "shippingState",
      //   label: "Location",
      //   width: 160,
      //   render: (_, row) => {
      //     const state = row?.shippingState || "-";
      //     const pincode = row?.shippingPostalCode || "";

      //     return pincode ? `${state} – ${pincode}` : state;
      //   },
      // },

      {
        key: "orderDate",
        label: "Order Date",
        width: 120,
        render: (value) =>
          value ? (
            <div className="date-cell">
              <span>{moment(value).format("DD MMM YYYY")}</span>
              <span className="date-time">{moment(value).format("HH:mm")}</span>
            </div>
          ) : (
            "-"
          ),
      },
      {
        key: "updatedAt",
        label: "Status Date",
        width: 140,
        render: (value) =>
          value ? (
            <div className="date-cell">
              <span>{moment(value).format("DD MMM YYYY")}</span>
              <span className="date-time">{moment(value).format("HH:mm")}</span>
            </div>
          ) : (
            "-"
          ),
      },
      // Additional columns for Mapped Orders (from originalExcelData)
      ...(activeTab === 'mapped' ? [
        {
          key: "remapAction",
          label: "Re-map",
          // label: "Actions",
          width: 100,
          render: (_, row) => {
            // Get ASIN from orderDetails
            const asin = row?.orderDetails?.[0]?.ASIN || row?.originalExcelData?.["ASIN"];
            if (!asin) return "-";

            // Get order value for margin calculation - check multiple possible fields
            const orderValue = parseFloat(
              row?.orderDetails?.[0]?.consumerPrice ||
              row?.originalExcelData?.["Order Value"] ||
              row?.totalConsumerPrice ||
              row?.consumerPrice ||
              0
            );
            const quantity = parseInt(row?.orderDetails?.[0]?.quantity || row?.originalExcelData?.["Qty"] || 1);

            // Get old/current mapped price (FBP) from variant - check multiple fields
            // Priority: breakdown.FBP > finalPrice > price (but price might be RMB, so check if it's reasonable)
            const variant = row?.orderDetails?.[0]?.variant || {};
            let oldPrice = parseFloat(
              variant?.breakdown?.FBP ||
              variant?.finalPrice ||
              0
            );

            // If oldPrice is 0 but we have a price field, check if it looks like INR (> 100 typically)
            if (oldPrice === 0 && variant?.price) {
              const priceVal = parseFloat(variant.price);
              // If price > 500, it's likely INR FBP, otherwise it might be RMB
              if (priceVal > 500) {
                oldPrice = priceVal;
              }
            }

            const oldSkuId = row?.orderDetails?.[0]?.sku || variant?.skuId || '';
            const oldOfferId = variant?.offerId || '';

            // Calculate old margin for display
            const oldMargin = orderValue > 0 && oldPrice > 0 ? orderValue - oldPrice : 0;

            // Get upsaleoOrderId for display - check multiple sources
            const upsaleoOrderId = row?.upsaleoOrderId || row?.originalExcelData?.["Order ID"] || row?.orderId || '';

            // Debug log
            console.log('[Remap Button] Order:', row?.orderId, 'upsaleoOrderId:', upsaleoOrderId, 'orderValue:', orderValue, 'oldPrice:', oldPrice, 'oldMargin:', oldMargin, 'row keys:', Object.keys(row || {}));

            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Open Amazon Product Detail page with remap mode, order value, and old price
                  const url = `/admin/amazon-product?asin=${asin}&remapOrderId=${row._id}&oid=${encodeURIComponent(upsaleoOrderId)}&orderValue=${orderValue}&orderQty=${quantity}&oldPrice=${oldPrice}&oldSkuId=${encodeURIComponent(oldSkuId)}&oldOfferId=${encodeURIComponent(oldOfferId)}`;
                  console.log('[Remap] Opening URL:', url);
                  window.open(url, "_blank");
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: 500,
                  border: '1px solid #f59e0b',
                  background: '#fffbeb',
                  color: '#b45309',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                title={`Re-map (Order: ₹${orderValue}, Old FBP: ₹${oldPrice}, Old Margin: ₹${oldMargin})`}
              >
                <i className="fas fa-sync-alt" style={{ fontSize: '10px' }}></i>
                Re-map
              </button>
            );
          },
        },
        {
          key: "excelEntryDate",
          label: "Entry Date",
          width: 120,
          render: (_, row) => {
            const val = row?.originalExcelData?.["Entry Date"];
            if (!val) return "-";
            // Excel serial date conversion
            if (typeof val === 'number') {
              const date = new Date((val - 25569) * 86400 * 1000);
              return moment(date).format("DD MMM YYYY");
            }
            return val;
          },
        },
        {
          key: "orderType",
          label: "Order Type",
          width: 100,
          render: (value, row) => row?.originalExcelData?.["Order Type"] || value || "-",
        },
        {
          key: "panel",
          label: "Panel",
          width: 130,
          render: (value, row) => row?.originalExcelData?.["Panel"] || value || "-",
        },
        {
          key: "excelOrderDate",
          label: "Excel Order Date",
          width: 130,
          render: (_, row) => {
            const val = row?.originalExcelData?.["Order Date"];
            if (!val) return "-";
            if (typeof val === 'number') {
              const date = new Date((val - 25569) * 86400 * 1000);
              return moment(date).format("DD MMM YYYY");
            }
            return val;
          },
        },
        {
          key: "excelSKU",
          label: "Excel SKU",
          width: 180,
          render: (_, row) => row?.originalExcelData?.["SKU"] || "-",
        },
        {
          key: "excelASIN",
          label: "Excel ASIN",
          width: 130,
          render: (_, row) => row?.originalExcelData?.["ASIN"] || "-",
        },
        {
          key: "excelURL",
          label: "URL",
          width: 100,
          render: (_, row) => {
            const url = row?.originalExcelData?.["URL"];
            if (!url) return "-";
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: '#3b82f6', textDecoration: 'none' }}
              >
                View
              </a>
            );
          },
        },
        {
          key: "excelQty",
          label: "Qty",
          width: 70,
          render: (_, row) => row?.originalExcelData?.["Qty"] || "-",
        },
        {
          key: "excelOrderValue",
          label: "Order Value",
          width: 120,
          render: (_, row) => {
            const val = row?.originalExcelData?.["Order Value"];
            return val ? formatCurrency(val) : "-";
          },
        },
        {
          key: "shipByDate",
          label: "Ship By Date",
          width: 120,
          render: (value, row) => {
            const excelVal = row?.originalExcelData?.["Ship By Date"];
            if (excelVal && typeof excelVal === 'number') {
              const date = new Date((excelVal - 25569) * 86400 * 1000);
              return moment(date).format("DD MMM YYYY");
            }
            return value ? moment(value).format("DD MMM YYYY") : "-";
          },
        },
        {
          key: "asinMappedAt",
          label: "Mapped At",
          width: 150,
          render: (value) => value ? (
            <div className="date-cell">
              <span>{moment(value).format("DD MMM YYYY")}</span>
              <span className="date-time">{moment(value).format("HH:mm")}</span>
            </div>
          ) : "-",
        },
        {
          key: "failedOrderId",
          label: "Failed Order ID",
          width: 130,
          render: (value) => value ? (
            <span style={{ fontSize: '11px', color: '#64748b' }}>
              {String(value).slice(-8)}
            </span>
          ) : "-",
        },
      ] : []),
    ];

    // Keep Actions column at the end for all tabs/views.
    const actionsColIndex = baseColumns.findIndex((col) => col?.key === "tickets");
    if (actionsColIndex !== -1) {
      const [actionsCol] = baseColumns.splice(actionsColIndex, 1);
      baseColumns.push(actionsCol);
    }

    return baseColumns;
  }, [activeTab, navigate, currentPage, effectivePageSize, canDelete, orderWarnings, sourceTab]);

  // Get only processing orders from selected
  const processingOrdersCount = selectedOrders.filter(o => o.status === "processing").length;
  // Get only payment pending orders from selected
  const paymentPendingOrdersCount = selectedOrders.filter(o => o.status === "payment pending").length;
  // Check if button should be enabled
  const isPayNowEnabled = paymentPendingOrdersCount > 0;

  const toolbarLeftActions = (
    <div className="actions-left" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* Order Type Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        <button
          onClick={() => onTabChange && onTabChange('all')}
          style={{
            display: 'flex',
            alignItems: 'center',
            // gap: '4px',
            padding: '6px 8px',
            fontSize: '0.7rem',
            height: '32px',
            border: activeTab === 'all' ? '1px solid #3b82f6' : '1px solid #d0d7de',
            background: activeTab === 'all' ? '#3b82f6' : '#fff',
            color: activeTab === 'all' ? '#fff' : '#24292f',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          Orders
        </button>
        <button
          onClick={() => onTabChange && onTabChange('mapped')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 6px',
            fontSize: '0.7rem',
            height: '32px',
            border: activeTab === 'mapped' ? '1px solid #3b82f6' : '1px solid #d0d7de',
            background: activeTab === 'mapped' ? '#3b82f6' : '#fff',
            color: activeTab === 'mapped' ? '#fff' : '#24292f',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          Mapped Orders
          {mappedOrdersCount > 0 && (
            <span style={{
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '10px',
              background: activeTab === 'mapped' ? 'rgba(255,255,255,0.25)' : '#e5e7eb',
              color: activeTab === 'mapped' ? '#fff' : '#6b7280',
              fontWeight: 600
            }}>
              {mappedOrdersCount}
            </span>
          )}
        </button>
        <div className="um-total-users" style={{
          display: "flex",
          alignItems: "center",
          padding: "0 2px",
          fontSize: "0.7rem",
          fontWeight: "600",
          color: "#24292f",
          whiteSpace: "nowrap"
        }}>
          Total Orders: <span style={{ color: "#0969da", marginLeft: "4px" }}>{pages.totalItems || 0}</span>
        </div>
      </div>

      {/* Account Type Filter Dropdown - Only show for Mapped Orders tab */}
      {activeTab === 'mapped' && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "6px"
        }}>
          <label style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "#24292f",
            whiteSpace: "nowrap"
          }}>
            {/* curruntly hidden */}
            {/* Account:  */}
          </label>
          <select
            value={accountTypeFilter || ''}
            onChange={(e) => onAccountTypeFilterChange && onAccountTypeFilterChange(e.target.value)}
            style={{
              padding: "4px",
              fontSize: "0.7rem",
              border: "1px solid #d0d7de",
              borderRadius: "4px",
              backgroundColor: "#fff",
              color: "#24292f",
              cursor: "pointer",
              width: "60px",
              height: "32px"
            }}
          >
            <option value="">All</option>
            <option value="own">BTPL</option>
            <option value="reseller">Reseller</option>
          </select>
        </div>
      )}

      {/* Pay Now Button - only show in Mapped Orders tab, always visible but disabled when no orders selected */}
      {activeTab === 'mapped' && canEdit("order-management") && (
        <button
          onClick={handleBulkPayNow}
          disabled={!isPayNowEnabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 6px',
            fontSize: '12px',
            height: '32px',
            border: isPayNowEnabled ? '1px solid #10b981' : '1px solid #d0d7de',
            background: isPayNowEnabled ? '#10b981' : '#f3f4f6',
            color: isPayNowEnabled ? '#fff' : '#9ca3af',
            borderRadius: '4px',
            cursor: isPayNowEnabled ? 'pointer' : 'not-allowed',
            fontWeight: 600,
            boxShadow: isPayNowEnabled ? '0 2px 4px rgba(16, 185, 129, 0.2)' : 'none',
            opacity: isPayNowEnabled ? 1 : 0.6,
            transition: 'all 0.2s ease'
          }}
          title={isPayNowEnabled ? "Process payment from sellers' wallets" : "Select payment pending orders to enable"}
        >
          {/* curruntly hidden enable later */}
          {/* <MdCreditCard /> */}
          Pay Now {isPayNowEnabled ? `(${paymentPendingOrdersCount})` : ''}
        </button>
      )}

      {/* <button 
        onClick={handleExportCsv} 
        style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          fontSize: '12px',
          height: '32px',
          border: '1px solid #d0d7de',
          background: '#fff',
          color: '#24292f',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        <i className="fas fa-download"></i>
        Export Orders
      </button> */}
      {/* <button 
        onClick={handleProcessExportCsv} 
        style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          fontSize: '12px',
          height: '32px',
          border: '1px solid #d0d7de',
          background: '#fff',
          color: '#24292f',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        <i className="fas fa-file-export"></i>
        Export Processed
      </button> */}
      <button
        onClick={handleProcessToCrossBorder}
        disabled={processingOrdersCount === 0 || isProcessingCrossBorder}
        className="process-cross-border-btn"
        title={isProcessingCrossBorder ? "Processing..." : (processingOrdersCount === 0 ? "Select processing orders to enable" : "Process selected orders to cross-border")}
        style={{
          display: 'flex',
          alignItems: 'center',
          // gap: '6px',
          padding: '6px 4px',
          fontSize: '0.7rem',
          height: '32px',
          border: (processingOrdersCount === 0 || isProcessingCrossBorder) ? '1px solid #d1d5db' : '1px solid #4361ee',
          background: isProcessingCrossBorder ? '#6b7280' : (processingOrdersCount === 0 ? '#f3f4f6' : '#4361ee'),
          color: (processingOrdersCount === 0 && !isProcessingCrossBorder) ? '#9ca3af' : '#fff',
          borderRadius: '4px',
          cursor: (processingOrdersCount === 0 || isProcessingCrossBorder) ? 'not-allowed' : 'pointer',
          opacity: (processingOrdersCount === 0 || isProcessingCrossBorder) ? 0.6 : 1,
          transition: 'all 0.2s'
        }}
      >
        {isProcessingCrossBorder ? (
          <>
            <div style={{
              width: '14px', height: '14px',
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            Processing...
          </>
        ) : (
          <>
            <i className="fas fa-globe"></i>
            Process to Cross-Border {processingOrdersCount > 0 && `(${processingOrdersCount})`}
          </>
        )}
      </button>
      {canDelete("order-management") && (
        <button
          onClick={handleBulkDeleteClick}
          disabled={selectedOrders.length === 0}
          title={selectedOrders.length === 0 ? "Select orders to delete" : "Delete selected orders (password required)"}
          style={{
            display: 'flex',
            alignItems: 'center',
            // gap: '6px',
            padding: '6px 4px',
            fontSize: '0.7rem',
            height: '32px',
            border: selectedOrders.length === 0 ? '1px solid #d1d5db' : '1px solid #dc2626',
            background: selectedOrders.length === 0 ? '#f3f4f6' : '#fff',
            color: selectedOrders.length === 0 ? '#9ca3af' : '#dc2626',
            borderRadius: '4px',
            cursor: selectedOrders.length === 0 ? 'not-allowed' : 'pointer',
            opacity: selectedOrders.length === 0 ? 0.6 : 1,
          }}
        >
          <i className="fas fa-trash"></i>
          Delete {selectedOrders.length > 0 && `(${selectedOrders.length})`}
        </button>
      )}
    </div>
  );

  const toolbarRightActions = (
    <div className="actions-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {canEdit("order-management") && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="file"
              onChange={handleFile}
              id="file-upload"
              style={{ display: 'none' }}
            />
            <label
              htmlFor="file-upload"
              style={{
                display: 'flex',
                alignItems: 'center',
                // gap: '6px',
                padding: '6px 4px',
                fontSize: '0.7rem',
                height: '32px',
                border: '1px solid #d0d7de',
                background: '#fff',
                color: '#24292f',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-upload"></i>
              Choose File
            </label>
            {uploadFile && (
              <span style={{ fontSize: '12px', color: '#656d76' }}>{uploadFile.name}</span>
            )}
          </div>
          <button
            onClick={handleTracking}
            style={{
              display: 'flex',
              alignItems: 'center',
              // gap: '6px',
              padding: '6px 4px',
              fontSize: '0.7rem',
              height: '32px',
              border: '1px solid #0969da',
              background: '#0969da',
              color: '#fff',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-upload"></i>
            Upload Tracking
          </button>
        </>
      )}
    </div>
  );

  // Fetch filter values from server for all pages
  const fetchFilterValues = async (columnKey) => {
    try {
      const res = await orderApiService.getOrderFilterValues(columnKey);
      if (res?.status?.code === 200 && res?.data?.values) {
        return res.data.values;
      }
      return [];
    } catch (err) {
      console.error('Error fetching filter values:', err);
      return [];
    }
  };

  return (
    <div className="order-table-container">
      {/* Payment Processing Loading Modal */}
      {isProcessingPayment && (
        <div className="payment-loading-overlay">
          <div className="payment-loading-content">
            <div className="payment-loading-spinner" />
            <h3 className="payment-loading-title">
              Processing Payment...
            </h3>
            <p className="payment-loading-text">
              Please wait while we process your payment
            </p>

            {/* Progress Bar */}
            <div className="payment-progress-container">
              <div className="payment-progress-bar">
                <div
                  className="payment-progress-fill"
                  style={{ width: `${paymentProgress}%` }}
                >
                  <span className="payment-progress-text">{Math.round(paymentProgress)}%</span>
                </div>
              </div>
              <div className="payment-progress-label">
                {paymentProgress < 30 && "Initializing payment..."}
                {paymentProgress >= 30 && paymentProgress < 60 && "Processing transactions..."}
                {paymentProgress >= 60 && paymentProgress < 90 && "Updating records..."}
                {paymentProgress >= 90 && paymentProgress < 100 && "Finalizing..."}
                {paymentProgress >= 100 && "Complete!"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Success Modal */}
      {showPaymentSuccessModal && paymentSuccessData && (
        <div
          className="payment-success-overlay"
          onClick={() => {
            setShowPaymentSuccessModal(false);
            window.location.reload();
          }}
        >
          <div
            className="payment-success-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header */}
            <div className={`payment-success-header ${paymentSuccessData.successCount > 0 ? 'payment-success-header-success' : 'payment-success-header-failed'}`}>
              <div className="payment-success-header-icon">
                {paymentSuccessData.successCount > 0 ? <MdCheckCircle /> : <MdError />}
              </div>
              <h2 className="payment-success-header-title">
                {paymentSuccessData.successCount > 0
                  ? 'Payment Processed Successfully!'
                  : 'Payment Processing Failed'
                }
              </h2>
              <p className="payment-success-header-subtitle">
                {paymentSuccessData.successCount > 0
                  ? `${paymentSuccessData.successCount} order${paymentSuccessData.successCount > 1 ? 's' : ''} paid successfully`
                  : 'No orders were processed'
                }
              </p>
            </div>

            {/* Summary Stats */}
            <div className="payment-success-summary">
              {paymentSuccessData.successCount > 0 && (
                <div className="payment-success-card payment-success-card-success">
                  <div className="payment-success-card-icon">
                    <MdCheckCircle className="icon-success" />
                  </div>
                  <div className="payment-success-card-value text-success-dark">
                    {paymentSuccessData.successCount}
                  </div>
                  <div className="payment-success-card-label text-success">
                    Successful
                  </div>
                </div>
              )}
              {paymentSuccessData.failedCount > 0 && (
                <div className="payment-success-card payment-success-card-failed">
                  <div className="payment-success-card-icon">
                    <MdError className="icon-error" />
                  </div>
                  <div className="payment-success-card-value text-error-dark">
                    {paymentSuccessData.failedCount}
                  </div>
                  <div className="payment-success-card-label text-error">
                    Failed
                  </div>
                </div>
              )}
              {paymentSuccessData.skippedCount > 0 && (
                <div className="payment-success-card payment-success-card-skipped">
                  <div className="payment-success-card-icon">
                    <MdWarning className="icon-warning" />
                  </div>
                  <div className="payment-success-card-value text-warning-dark">
                    {paymentSuccessData.skippedCount}
                  </div>
                  <div className="payment-success-card-label text-warning">
                    Skipped
                  </div>
                </div>
              )}
              {paymentSuccessData.totalAmount > 0 && (
                <div className="payment-success-card payment-success-card-total">
                  <div className="payment-success-card-icon">
                    <MdAccountBalanceWallet className="icon-white" />
                  </div>
                  <div className="payment-success-card-value text-white">
                    {formatCurrency(paymentSuccessData.totalAmount)}
                  </div>
                  <div className="payment-success-card-label text-white-90">
                    Total Paid
                  </div>
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="payment-success-details payment-success-details-scroll custom-scrollbar">
              {/* Successful Orders */}
              {paymentSuccessData.results?.success?.length > 0 && (
                <div className="payment-success-section-success">
                  <h3 className="payment-success-section-success-title">
                    <MdCheckCircle className="icon-md" />
                    Successful Payments ({paymentSuccessData.results.success.length})
                  </h3>
                  <div className="payment-success-orders-success-list">
                    {paymentSuccessData.results.success.slice(0, showAllSuccessOrders ? undefined : 5).map((item, index) => {
                      // Find seller info from confirmation data
                      const sellerInfo = paymentConfirmData?.sellerPayments?.find(
                        s => s.sellerId === item.sellerId
                      );

                      return (
                        <div key={index} className="payment-success-order-success-card">
                          <div className="payment-success-order-success-info">
                            <div className="payment-success-order-success-id">
                              {item.upsaleoOrderId}
                            </div>
                            <div className="payment-success-order-success-seller">
                              {sellerInfo?.sellerName || 'Unknown Seller'}
                            </div>
                            {sellerInfo?.sellerEmail && (
                              <div className="payment-success-order-success-email">
                                {sellerInfo.sellerEmail}
                              </div>
                            )}
                          </div>
                          <div className="payment-success-order-success-amount">
                            {formatCurrency(item.amount)}
                          </div>
                        </div>
                      );
                    })}
                    {!showAllSuccessOrders && paymentSuccessData.results.success.length > 5 && (
                      <div
                        onClick={() => setShowAllSuccessOrders(true)}
                        className="payment-success-show-more-link"
                      >
                        ... and {paymentSuccessData.results.success.length - 5} more (click to show all)
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Failed Orders */}
              {paymentSuccessData.results?.failed?.length > 0 && (
                <div className="payment-success-section-failed">
                  <h3 className="payment-success-section-failed-title">
                    <MdError className="icon-md" />
                    Failed Payments ({paymentSuccessData.results.failed.length})
                  </h3>
                  <div className="payment-success-orders-failed-list">
                    {paymentSuccessData.results.failed.slice(0, showAllFailedOrders ? undefined : 5).map((item, index) => {
                      // Find seller info from confirmation data
                      const sellerInfo = paymentConfirmData?.sellerPayments?.find(
                        s => s.sellerId === item.sellerId
                      );

                      return (
                        <div key={index} className="payment-success-order-failed-card">
                          <div className="payment-success-order-failed-header">
                            <div className="payment-success-order-failed-info">
                              <div className="payment-success-order-failed-id">
                                {item.upsaleoOrderId || item.orderId}
                              </div>
                              <div className="payment-success-order-failed-seller">
                                {sellerInfo?.sellerName || 'Unknown Seller'}
                              </div>
                              {sellerInfo?.sellerEmail && (
                                <div className="payment-success-order-failed-email">
                                  {sellerInfo.sellerEmail}
                                </div>
                              )}
                            </div>
                            {item.amount && (
                              <div className="payment-success-order-failed-amount">
                                {formatCurrency(item.amount)}
                              </div>
                            )}
                          </div>
                          <div className="payment-success-order-failed-reason">
                            {item.reason}
                          </div>
                        </div>
                      );
                    })}
                    {!showAllFailedOrders && paymentSuccessData.results.failed.length > 5 && (
                      <div
                        onClick={() => setShowAllFailedOrders(true)}
                        className="payment-success-show-more-link-failed"
                      >
                        ... and {paymentSuccessData.results.failed.length - 5} more (click to show all)
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Skipped Orders */}
              {paymentSuccessData.results?.skipped?.length > 0 && (
                <div className="payment-success-section-skipped">
                  <h3 className="payment-success-section-skipped-title">
                    <MdWarning className="icon-md" />
                    Skipped Orders ({paymentSuccessData.results.skipped.length})
                  </h3>
                  <div className="payment-success-orders-skipped-list">
                    {paymentSuccessData.results.skipped.slice(0, 3).map((item, index) => (
                      <div key={index} className="payment-success-order-skipped-card">
                        <div className="payment-success-order-skipped-id">
                          {item.upsaleoOrderId || item.orderId}
                        </div>
                        <div className="payment-success-order-skipped-reason">
                          {item.reason}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="payment-success-footer">
              <button
                onClick={async () => {
                  setShowPaymentSuccessModal(false);
                  setSelectedOrders([]); // Clear selection

                  // Refresh data instead of page reload
                  if (onFetchAllOrders) {
                    loader.showInlineLoader("Refreshing orders...", "small");
                    try {
                      await onFetchAllOrders();
                    } catch (err) {
                      console.error('[Refresh Error]', err);
                    } finally {
                      loader.hideLoader();
                    }
                  }
                }}
                className="payment-success-btn-close"
              >
                <MdCheckCircle className="icon-sm" />
                Close & Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Confirmation Modal */}
      {showPaymentConfirmModal && paymentConfirmData && (
        <div
          className="payment-confirm-overlay"
          onClick={() => setShowPaymentConfirmModal(false)}
        >
          <div
            className="payment-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="payment-confirm-header">
              <div className="payment-confirm-header-content">
                <div className="payment-confirm-header-left">
                  <div className="payment-confirm-icon">
                    <MdCreditCard />
                  </div>
                  <div>
                    <h2 className="payment-confirm-title">
                      Confirm Bulk Payment
                    </h2>
                    <p className="payment-confirm-subtitle">
                      {paymentConfirmData.filteredOutCount > 0
                        ? `Processing ${paymentConfirmData.orderCount} payment pending orders (${paymentConfirmData.filteredOutCount} order${paymentConfirmData.filteredOutCount > 1 ? 's' : ''} filtered out)`
                        : 'Review payment details before processing'
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPaymentConfirmModal(false)}
                  className="payment-confirm-close-btn"
                >
                  <MdClose />
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="payment-confirm-summary">
              {/* Info banner if orders were filtered */}
              {paymentConfirmData.filteredOutCount > 0 && (
                <div className="payment-confirm-info-banner">
                  <MdInfo className="icon-warning icon-sm" />
                  <span>
                    <strong>{paymentConfirmData.filteredOutCount}</strong> order{paymentConfirmData.filteredOutCount > 1 ? 's were' : ' was'} filtered out (not payment pending status)
                  </span>
                </div>
              )}

              <div className="payment-confirm-card">
                <div className="payment-confirm-card-label">
                  Payment Pending
                </div>
                <div className="payment-confirm-card-value">
                  {paymentConfirmData.orderCount}
                </div>
              </div>
              <div className="payment-confirm-card">
                <div className="payment-confirm-card-label">
                  Sellers
                </div>
                <div className="payment-confirm-card-value">
                  {paymentConfirmData.sellerCount}
                </div>
              </div>
              <div className="payment-confirm-card-total">
                <div className="payment-confirm-card-total-label">
                  Total Amount
                </div>
                <div className="payment-confirm-card-total-value">
                  {formatCurrency(paymentConfirmData.grandTotal)}
                </div>
              </div>
            </div>

            {/* Seller Details */}
            <div className="payment-confirm-details custom-scrollbar">
              <h3 className="payment-confirm-section-title">
                <MdPeople className="icon-success icon-md" />
                Seller Payment Breakdown
              </h3>

              <div className="payment-confirm-sellers-list">
                {paymentConfirmData.sellerPayments.map((seller, index) => (
                  <div
                    key={index}
                    className="payment-confirm-seller-card"
                  >
                    {/* Seller Info - Single Row Layout */}
                    <div className="payment-confirm-seller-info">
                      {/* Seller Name & Email */}
                      <div className="payment-confirm-seller-details">
                        <div className="payment-confirm-seller-name">
                          {seller.sellerName || 'Unknown Seller'}
                        </div>
                        <div className="payment-confirm-seller-email">
                          {seller.sellerEmail}
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="payment-confirm-divider"></div>

                      {/* IM Cash (Wallet) */}
                      <div className="payment-confirm-wallet">
                        <div className="payment-confirm-wallet-label">
                          IM Cash
                        </div>
                        <div className="payment-confirm-wallet-value" style={{
                          color: (seller.walletBalance || 0) < seller.total ? '#dc2626' : '#059669'
                        }}>
                          <MdAccountBalanceWallet className="icon-sm" />
                          {formatCurrency(seller.walletBalance || 0)}
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="payment-confirm-divider"></div>

                      {/* Order Value */}
                      <div className="payment-confirm-order-value">
                        <div className="payment-confirm-order-value-label">
                          Order Value
                        </div>
                        <div className="payment-confirm-order-value-amount">
                          {formatCurrency(seller.total)}
                        </div>
                      </div>
                    </div>

                    {/* Orders List */}
                    <div className="payment-confirm-orders-list">
                      {seller.orders.map((order, orderIndex) => (
                        <div
                          key={orderIndex}
                          className="payment-confirm-order-item"
                        >
                          <div className="payment-confirm-order-ids">
                            <span className="payment-confirm-order-id-main">
                              {order.upsaleoOrderId}
                            </span>
                            <span className="payment-confirm-order-id-sub">
                              {order.orderId}
                            </span>
                          </div>
                          <span className="payment-confirm-order-amount">
                            {formatCurrency(order.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="payment-confirm-footer">
              <button
                onClick={() => setShowPaymentConfirmModal(false)}
                className="payment-confirm-btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPayment}
                className="payment-confirm-btn-submit"
              >
                <MdCheckCircle className="icon-sm" />
                Confirm & Process Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Delete Modal */}
      {deleteModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setDeleteModal({ open: false, order: null, password: "", showPassword: false })}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              width: "400px",
              maxWidth: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 600, color: "#dc2626" }}>
              Delete Order
            </h3>
            <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#6b7280" }}>
              Order: <strong>{deleteModal.order?.upsaleoOrderId || deleteModal.order?.orderId}</strong>
            </p>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#dc2626" }}>
              This action cannot be undone. Please enter your admin password to confirm.
            </p>
            <div style={{ position: "relative", marginBottom: "16px" }}>
              <input
                type={deleteModal.showPassword ? "text" : "password"}
                value={deleteModal.password}
                onChange={(e) => setDeleteModal({ ...deleteModal, password: e.target.value })}
                placeholder="Enter admin password..."
                autoComplete="new-password"
                style={{
                  width: "100%",
                  padding: "8px 40px 8px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleDeleteConfirm();
                }}
              />
              <button
                type="button"
                onClick={() => setDeleteModal({ ...deleteModal, showPassword: !deleteModal.showPassword })}
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "12px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
                title={deleteModal.showPassword ? "Hide password" : "Show password"}
              >
                {deleteModal.showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteModal({ open: false, order: null, password: "", showPassword: false })}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={!deleteModal.password}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: deleteModal.password ? "#dc2626" : "#9ca3af",
                  color: "#fff",
                  borderRadius: "4px",
                  cursor: deleteModal.password ? "pointer" : "not-allowed",
                  fontSize: "14px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {bulkDeleteModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setBulkDeleteModal({ open: false, password: "", showPassword: false })}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              width: "450px",
              maxWidth: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 600, color: "#dc2626" }}>
              Bulk Delete Orders
            </h3>
            <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#6b7280" }}>
              You are about to delete <strong>{selectedOrders.length}</strong> order(s).
            </p>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#dc2626" }}>
              This action cannot be undone. Please enter your admin password to confirm.
            </p>
            <div style={{ position: "relative", marginBottom: "16px" }}>
              <input
                type={bulkDeleteModal.showPassword ? "text" : "password"}
                value={bulkDeleteModal.password}
                onChange={(e) => setBulkDeleteModal({ ...bulkDeleteModal, password: e.target.value })}
                placeholder="Enter admin password..."
                autoComplete="new-password"
                style={{
                  width: "100%",
                  padding: "8px 40px 8px 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleBulkDeleteConfirm();
                }}
              />
              <button
                type="button"
                onClick={() => setBulkDeleteModal({ ...bulkDeleteModal, showPassword: !bulkDeleteModal.showPassword })}
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "12px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
                title={bulkDeleteModal.showPassword ? "Hide password" : "Show password"}
              >
                {bulkDeleteModal.showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setBulkDeleteModal({ open: false, password: "", showPassword: false })}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDeleteConfirm}
                disabled={!bulkDeleteModal.password}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: bulkDeleteModal.password ? "#dc2626" : "#9ca3af",
                  color: "#fff",
                  borderRadius: "4px",
                  cursor: bulkDeleteModal.password ? "pointer" : "not-allowed",
                  fontSize: "14px",
                }}
              >
                Delete {selectedOrders.length} Order(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split Order Modal - uses full order from getOrderById so product/offerId are available */}
      {splitModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => !splitSubmitting && !splitModal.loading && setSplitModal({ open: false, order: null, selectedOfferIds: [], loading: false })}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              width: "480px",
              maxWidth: "90%",
              maxHeight: "85vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", fontWeight: 600, color: "#f59e0b" }}>
              Split order
            </h3>
            {splitModal.loading ? (
              <p style={{ margin: "16px 0", fontSize: "14px", color: "#6b7280" }}>Loading order details…</p>
            ) : splitModal.order ? (
              <>
                <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#6b7280" }}>
                  Order: <strong>{splitModal.order?.upsaleoOrderId || splitModal.order?.orderId}</strong>. Select which offer ID(s) to move into a <strong>new order</strong>. The rest stay in this order.
                </p>
                {(() => {
                  const groups = getOrderDetailsGroupedByOfferId(splitModal.order);
                  const canSplit = groups.length >= 2;
                  return !canSplit ? (
                    <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#b45309", background: "#fffbeb", padding: "12px", borderRadius: "6px" }}>
                      This order has only one offer ID (or none). Splitting requires at least 2 different offer IDs in the order.
                    </p>
                  ) : null;
                })()}
                <div style={{ marginBottom: "16px" }}>
                  {getOrderDetailsGroupedByOfferId(splitModal.order).map((group) => {
                    const isSelected = (splitModal.selectedOfferIds || []).includes(group.offerId);
                    return (
                      <div
                        key={group.offerId}
                        style={{
                          border: `1px solid ${isSelected ? "#f59e0b" : "#e5e7eb"}`,
                          borderRadius: "6px",
                          padding: "12px",
                          marginBottom: "8px",
                          background: isSelected ? "#fffbeb" : "#fff",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const current = splitModal.selectedOfferIds || [];
                          const next = current.includes(group.offerId)
                            ? current.filter((id) => id !== group.offerId)
                            : [...current, group.offerId];
                          setSplitModal({ ...splitModal, selectedOfferIds: next });
                        }}
                      >
                        <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", margin: 0 }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => { }}
                            style={{ width: "18px", height: "18px" }}
                          />
                          <span style={{ fontWeight: 600, color: "#111" }}>Offer ID: {group.offerId}</span>
                        </label>
                        <div style={{ marginTop: "6px", marginLeft: "26px", fontSize: "13px", color: "#6b7280" }}>
                          {group.items.map((item, i) => (
                            <div key={i}>
                              {item.productName || item.product?.title || item.sku || "Item"} × {item.quantity ?? 1}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setSplitModal({ open: false, order: null, selectedOfferIds: [], loading: false })}
                    disabled={splitSubmitting}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #d1d5db",
                      background: "#fff",
                      borderRadius: "4px",
                      cursor: splitSubmitting ? "not-allowed" : "pointer",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSplitConfirm}
                    disabled={splitSubmitting || (splitModal.selectedOfferIds || []).length === 0 || getOrderDetailsGroupedByOfferId(splitModal.order).length < 2}
                    style={{
                      padding: "8px 16px",
                      border: "none",
                      background: (splitModal.selectedOfferIds || []).length > 0 && !splitSubmitting && getOrderDetailsGroupedByOfferId(splitModal.order).length >= 2 ? "#f59e0b" : "#9ca3af",
                      color: "#fff",
                      borderRadius: "4px",
                      cursor: (splitModal.selectedOfferIds || []).length > 0 && !splitSubmitting && getOrderDetailsGroupedByOfferId(splitModal.order).length >= 2 ? "pointer" : "not-allowed",
                      fontSize: "14px",
                    }}
                  >
                    {splitSubmitting ? "Splitting…" : "Create new order with selected"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Reject Order: Refund Modal (same as ticket-management) - key forces remount when opening */}
      <RefundModal
        key={rejectRefundOrder ? `reject-refund-${rejectRefundOrder.orderId}` : 'reject-refund-closed'}
        isOpen={!!rejectRefundOrder}
        onRequestClose={() => setRejectRefundOrder(null)}
        orderData={rejectRefundOrder}
        onSuccess={handleRejectRefundSuccess}
        variant={rejectRefundOrder?.variant}
      />

      {/* Confirm / Reject Order Modal: confirm = API only; reject = then open Refund modal */}
      {confirmRejectModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCancelConfirmReject}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              width: "450px",
              maxWidth: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: "0 0 16px 0",
              fontSize: "18px",
              fontWeight: 600,
              color: confirmRejectModal.action === 'confirm' ? "#10b981" : "#ef4444"
            }}>
              {confirmRejectModal.action === 'confirm' ? 'Confirm Order' : 'Reject Order'}
            </h3>

            <div style={{ marginBottom: "16px" }}>
              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                Order: <strong>{confirmRejectModal.order?.upsaleoOrderId || confirmRejectModal.order?.orderId}</strong>
              </p>
              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                Status: <strong>{confirmRejectModal.order?.status}</strong>
              </p>
              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                Importerr Price: <strong>{formatCurrency(confirmRejectModal.order?.importerPrice || 0)}</strong>
              </p>
              <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                Source of payment: <strong>
                  {confirmRejectModal.order?.razorpayPaymentId || confirmRejectModal.order?.razorpayOrderId
                    ? "Razorpay"
                    : confirmRejectModal.order?.transactionId
                      ? "Wallet"
                      : "—"}
                </strong>
              </p>
              {confirmRejectModal.order?.sellerId && (
                <p style={{ margin: "0 0 8px 0", fontSize: "14px", color: "#6b7280" }}>
                  Seller: <strong>
                    {confirmRejectModal.order.sellerId.firstName || ""} {confirmRejectModal.order.sellerId.lastName || ""}
                  </strong>
                </p>
              )}
            </div>

            <p style={{
              margin: "0 0 20px 0",
              fontSize: "14px",
              color: confirmRejectModal.action === 'confirm' ? "#10b981" : "#ef4444",
              fontWeight: "500"
            }}>
              Are you sure you want to {confirmRejectModal.action} this order?
              {confirmRejectModal.action === 'confirm'
                ? " This will move the order forward in the processing pipeline."
                : " This will cancel the order. The order amount will be refunded to the user's wallet."
              }
            </p>

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={handleCancelConfirmReject}
                disabled={isProcessingAction}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: "4px",
                  cursor: isProcessingAction ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: isProcessingAction ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRejectModal.action === 'reject' ? handleRejectConfirmThenOpenRefund : handleConfirmRejectAction}
                disabled={confirmRejectModal.action === 'confirm' ? isProcessingAction : false}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  background: confirmRejectModal.action === 'reject'
                    ? "#ef4444"
                    : isProcessingAction ? "#9ca3af" : "#10b981",
                  color: "#fff",
                  borderRadius: "4px",
                  cursor: confirmRejectModal.action === 'reject' ? "pointer" : (isProcessingAction ? "not-allowed" : "pointer"),
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: confirmRejectModal.action === 'reject' ? 1 : (isProcessingAction ? 0.6 : 1),
                }}
              >
                {confirmRejectModal.action === 'confirm' && isProcessingAction ? (
                  <>
                    <div style={{
                      width: "14px",
                      height: "14px",
                      border: "2px solid #fff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite"
                    }}></div>
                    Processing...
                  </>
                ) : (
                  <>
                    {confirmRejectModal.action === 'confirm' ? <MdCheckCircle /> : <MdClose />}
                    {confirmRejectModal.action === 'confirm' ? 'Confirm Order' : 'Reject Order'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* <div className="table-wrapper"> */}
      <ExcelTable
        key={`orders-table-${activeTab}`}
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        loadingText="Loading orders..."
        showToolbarExport={true}
        showToolbarFilter={true}
        showToolbarColumns={true}
        showToolbarFullscreen={true}
        toolbarLeftActions={toolbarLeftActions}
        toolbarRightActions={toolbarRightActions}
        onSelectionChange={handleSelectionChange}
        selectedData={selectedOrders}
        selectable={true}
        sortable={true}
        resizable={true}
        showToolbar={true}
        title={activeTab === 'mapped' ? "Mapped Orders" : "Orders"}
        onFetchFilterValues={fetchFilterValues}
        onFilterChange={onColumnFilterChange}
        getRowClassName={(row) => {
          const key = row.upsaleoOrderId || row.orderId;
          if (key && orderWarnings[key]) {
            return 'row-skipped-supplier';
          }
          return '';
        }}
        getRowTitle={(row) => {
          const key = row.upsaleoOrderId || row.orderId;
          if (key && orderWarnings[key]) {
            return '⚠ Cross-border issues:\n' + orderWarnings[key].join('\n');
          }
          return undefined;
        }}
        customExportOptions={useMemo(() => [
          {
            type: 'date-range',
            dateRangeComponent: (
              <div style={{ padding: "8px", borderBottom: "1px solid #e5e7eb" }}>
                <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: "600", color: "#24292f" }}>
                  Date Range Export
                </label>
                <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                  <input
                    type="date"
                    value={exportDateRange.startDate}
                    onChange={(e) => setExportDateRange({ ...exportDateRange, startDate: e.target.value })}
                    style={{ flex: 1, padding: "6px", fontSize: "12px", border: "1px solid #d0d7de", borderRadius: "4px" }}
                  />
                  <input
                    type="date"
                    value={exportDateRange.endDate}
                    onChange={(e) => setExportDateRange({ ...exportDateRange, endDate: e.target.value })}
                    style={{ flex: 1, padding: "6px", fontSize: "12px", border: "1px solid #d0d7de", borderRadius: "4px" }}
                  />
                </div>
                <button
                  onClick={handleExportDateWise}
                  disabled={exporting || !exportDateRange.startDate || !exportDateRange.endDate}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    backgroundColor: "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    height: "32px",
                    opacity: (exporting || !exportDateRange.startDate || !exportDateRange.endDate) ? 0.6 : 1
                  }}
                >
                  <MdCalendarToday /> Export Date Wise
                </button>
              </div>
            )
          },
          {
            type: 'divider'
          },
          {
            type: 'custom',
            component: (
              <div style={{ padding: "8px" }}>
                <button
                  onClick={handleExportAllOrders}
                  disabled={exporting}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    backgroundColor: "#2196F3",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    height: "32px",
                    opacity: exporting ? 0.6 : 1
                  }}
                >
                  <MdFileDownload /> Export All Orders
                </button>
              </div>
            )
          }
        ], [exportDateRange, exporting])}
      />
      {ticketModalOrderId && (
        <AdminRaiseTicketModal
          modalIsOpen={!!ticketModalOrderId}
          setIsOpen={(open) => !open && setTicketModalOrderId(null)}
          orderId={ticketModalOrderId}
          handleRefresh={() => {
            setTicketModalOrderId(null);
            if (onRefresh) onRefresh();
          }}
        />
      )}
      {ticketPanelTicketId && (
        <TicketChatPanel
          ticketId={ticketPanelTicketId}
          orderLabel={ticketPanelOrderLabel}
          onClose={() => {
            setTicketPanelTicketId(null);
            setTicketPanelOrderLabel(null);
          }}
        />
      )}
      {/* Bank Transfer Confirm Modal */}
      {btConfirmModal.open && btConfirmModal.order && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
          }}
          onClick={() => !btConfirmModal.submitting && setBtConfirmModal({ open: false, order: null, adminNote: "", submitting: false })}
        >
          <div
            style={{
              background: '#fff', borderRadius: '14px', padding: '28px', maxWidth: '480px',
              width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <MdCheckCircle size={24} color="#3dd0e5" />
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a202c' }}>
                Confirm Bank Transfer
              </h3>
            </div>

            <p style={{ margin: '0 0 12px', color: '#374151', fontSize: '14px' }}>
              Order: <strong>{btConfirmModal.order.upsaleoOrderId || btConfirmModal.order.orderId}</strong>
            </p>

            {btConfirmModal.order.bankTransferTxnId && (
              <div style={{ background: '#f0fdfe', border: '1px solid #a5f3fc', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#0891b2', fontWeight: '600' }}>Transaction Reference ID</p>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#0e7490', wordBreak: 'break-all' }}>
                  {btConfirmModal.order.bankTransferTxnId}
                </p>
              </div>
            )}

            {btConfirmModal.order.paymentProofUrl && (
              <div style={{ marginBottom: '12px' }}>
                <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Payment Proof</p>
                {btConfirmModal.order.paymentProofUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <img
                    src={btConfirmModal.order.paymentProofUrl}
                    alt="Payment proof"
                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                ) : (
                  <a
                    href={btConfirmModal.order.paymentProofUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#3dd0e5', fontSize: '13px' }}
                  >
                    View Payment Proof
                  </a>
                )}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Admin Note (optional)
              </label>
              <textarea
                value={btConfirmModal.adminNote}
                onChange={(e) => setBtConfirmModal((prev) => ({ ...prev, adminNote: e.target.value }))}
                placeholder="Add a note about this confirmation…"
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0',
                  borderRadius: '8px', padding: '10px 12px', fontSize: '13px',
                  outline: 'none', resize: 'vertical', fontFamily: 'inherit'
                }}
                disabled={btConfirmModal.submitting}
              />
            </div>

            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '10px 14px' }}>
              This will move the order to <strong>Processing</strong> status and deduct inventory.
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => !btConfirmModal.submitting && setBtConfirmModal({ open: false, order: null, adminNote: "", submitting: false })}
                disabled={btConfirmModal.submitting}
                style={{
                  padding: '10px 20px', background: '#f1f5f9', border: '1px solid #e2e8f0',
                  borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#374151',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBtConfirmSubmit}
                disabled={btConfirmModal.submitting}
                style={{
                  padding: '10px 20px', background: '#3dd0e5', border: 'none',
                  borderRadius: '8px', fontSize: '13px', fontWeight: '700', color: '#fff',
                  cursor: 'pointer', opacity: btConfirmModal.submitting ? 0.7 : 1
                }}
              >
                {btConfirmModal.submitting ? 'Confirming…' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* </div> */}
    </div>
  );
};

export default OrderDetailTable;
